<template>
    <v-container fluid class="mt-1">
        <v-row>
            <v-col cols="12">
                <div class="d-flex align-center mb-3">
                    <h3 class="mb-0 mr-4">Attendance</h3>
                    <v-switch v-model="start_from_event_date" dense hide-details class="mt-0">
                        <template v-slot:label>
                            <span style="font-size:14px;">
                                {{ start_from_event_date ? 'Starting from Event Date' : 'Starting from Sunday' }}
                            </span>
                        </template>
                    </v-switch>
                    <v-switch v-if="combined_builds.some(hasPackageSplit)" v-model="show_package_split" dense
                        hide-details class="mt-0 ml-6">
                        <template v-slot:label>
                            <span style="font-size:14px;">Show per package</span>
                        </template>
                    </v-switch>
                </div>
                <v-data-table :headers="attendance_headers" :items="combined_builds" disable-pagination
                    hide-default-footer
                    style="font-family: Manrope;border:solid 1px #E0E0E0;">
                    <template v-slot:body="{ items }">
                        <tbody>
                            <template v-for="item in items">
                                <tr :key="item.id" class="attendance-row"
                                    :class="{ 'attendance-row--shared': show_package_split && hasPackageSplit(item) }">
                                    <td>
                                        {{ item.supplier.supplier_name }} - {{ item.option?.name }}
                                        <span v-if="hasPackageSplit(item)" class="attendance-shared-badge">
                                            {{ packageMembers(item).length }} packages
                                        </span>
                                    </td>
                                    <td style="text-align:center;" v-for="(day, index) in eventDaysOfWeek" :key="index">
                                        <div :set="attendees = getAttendanceOnDay(item, day)">
                                            <b v-if="attendees > 0">{{ attendees }}</b>
                                            <span v-else style="color: #BDBDBD;">0</span>
                                        </div>
                                    </td>
                                    <td>{{ get_selected_company.currency }}{{ item.rate }}</td>
                                    <td><b>{{ get_selected_company.currency }}{{ getAttendanceSubtotal(item).toFixed(2) }}</b></td>
                                </tr>
                                <!-- Shared by several packages (Copy Quote variants): each package's share -->
                                <template v-if="show_package_split && hasPackageSplit(item)">
                                    <tr v-for="member in packageMembers(item)" :key="'pkg-' + item.id + '-' + member.id"
                                        class="attendance-subrow">
                                        <td>
                                            <v-icon x-small color="#9E9E9E" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                            {{ member.quote ? member.quote.name : 'No quote' }}
                                        </td>
                                        <td style="text-align:center;" v-for="(day, index) in eventDaysOfWeek" :key="index">
                                            <span :set="attendees = getMemberAttendanceOnDay(member, day)"
                                                :class="attendees > 0 ? 'attendance-subrow-count' : 'attendance-subrow-empty'">
                                                {{ hasAttendanceOnDay(member, day) ? attendees : '–' }}
                                            </span>
                                        </td>
                                        <td>{{ get_selected_company.currency }}{{ member.rate }}</td>
                                        <td>{{ get_selected_company.currency }}{{ getMemberSubtotal(member).toFixed(2) }}</td>
                                    </tr>
                                </template>
                            </template>
                        </tbody>
                    </template>
                </v-data-table>
                <div class="mt-1 mr-10" style="text-align:right;">
                    Total Paid: <b>{{ get_selected_company.currency }}{{ total.toFixed(2) }}</b>
                </div>
            </v-col>
            <v-col cols="12" class="py-0">
                <v-btn class="custom-buttons mb-4 mr-2" color="#3988AA" :dark="event.event_status === 'Booking Closed'"
                    :disabled="event.event_status !== 'Booking Closed'" @click="openFinalGroupsizeDialog">
                    <v-icon small left>mdi-account-multiple-check</v-icon>
                    Update Final Groupsizes
                </v-btn>
                <v-btn class="custom-buttons mb-4 mr-2" color="#27AE60"
                    :dark="event.event_status === 'Booking Closed' && hasConfirmedBuilds"
                    :disabled="event.event_status !== 'Booking Closed' || !hasConfirmedBuilds"
                    :loading="loading_groupsize_emails" @click="openFinalGroupsizeEmails">
                    <v-icon small left>mdi-email</v-icon>
                    Send Final Groupsizes to Suppliers
                </v-btn>
                <!-- Every supplier at once: a template, or a booking change email (SUPPLIER_CHANGE_EMAILS.md);
                     untick suppliers or services in the dialog -->
                <SupplierTemplateMenu :templates="email_templates" :loading="loading_templates"
                    :disabled="builds.length === 0 && deleted_builds.length === 0"
                    :cancellation-disabled="deleted_builds.length === 0"
                    availability @availability="openAvailability()"
                    @select="openTemplateEmails($event)" @change="openChangeEmails($event)">
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn class="custom-buttons mb-4" color="#3988AA"
                            :dark="builds.length > 0 || deleted_builds.length > 0"
                            :disabled="builds.length === 0 && deleted_builds.length === 0" v-bind="attrs" v-on="on">
                            <v-icon small left>mdi-email-multiple-outline</v-icon>
                            Email Suppliers{{ tickedIn(combined_builds).length ? ` (${tickedIn(combined_builds).length})` : '' }}
                            <v-icon small right>mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                </SupplierTemplateMenu>
                <v-btn :disabled="deleted_builds.length == 0 || loading_deleted_builds" class="custom-buttons mb-4 ml-2"
                    text elevation="0" @click="openViewDeletedDialog">
                    <v-icon small left>mdi-delete-restore</v-icon>
                    <label v-if="loading_deleted_builds" style="cursor: pointer;">
                        Loading...
                    </label>
                    <label v-else-if="deleted_builds.length == 0" style="cursor: pointer;">
                        No Deleted Services
                    </label>
                    <label v-else style="cursor: pointer;">
                        View Deleted Services
                    </label>
                </v-btn>
            </v-col>
            <v-col cols="5" v-if="event.event_status !== 'Booking Closed'"
                style="margin-top:-15px;margin-bottom:-15px;">
                <v-alert type="info" outlined style="font-size:12px;">
                    Final groupsizes can be updated and sent once the event is closed for booking. Current
                    status is:
                    <b>{{ event.event_status }}</b>.
                </v-alert>
            </v-col>
            <v-col cols="5" v-else-if="!hasConfirmedBuilds" style="margin-top:-15px;margin-bottom:-15px;">
                <v-alert type="info" outlined style="font-size:12px;">
                    Final groupsizes can be sent once there are one or more Confirmed bookings.
                </v-alert>
            </v-col>
            <v-col cols="12">
                <v-expansion-panels v-model="open_panels" multiple class="custom-expansion-panels"
                    style="font-family: Manrope;">
                    <v-expansion-panel v-for="(grouped_builds, index) in builds" :key="index"
                        class="custom-expansion-panel mb-7">
                        <v-expansion-panel-header class="custom-expansion-header" :id="'panel' + index">
                            {{ grouped_builds[0].supplier.supplier_name }}
                            <label style="font-weight:400!important;margin-left:15px;text-transform:initial;">
                                ({{ groupQuoteNames(grouped_builds).length > 1 ? 'Quotes' : 'Quote' }}: {{ groupQuoteNames(grouped_builds).join(', ') || 'No Quote' }})
                            </label>
                        </v-expansion-panel-header>
                        <v-expansion-panel-content class="custom-expansion-panel-content">
                            <div class="supplier-panel-body">
                                <div class="supplier-panel-top-row">
                                    <div class="supplier-info-chip mr-2">
                                        <v-icon size="15" color="#3988AA" class="mr-2">mdi-email-outline</v-icon>
                                        <span class="supplier-info-label mr-2">EMAIL</span>
                                        <span class="supplier-info-value">
                                            {{ grouped_builds[0].supplier.email || '—' }}
                                        </span>
                                        <v-btn icon x-small class="ml-1" @click="openEditSupplierEmailDialog(grouped_builds[0].supplier)">
                                            <v-icon size="15" color="#3988AA">mdi-pencil-outline</v-icon>
                                        </v-btn>
                                    </div>
                                    <div class="supplier-actions-col">
                                        <v-btn class="custom-buttons mr-2" outlined color="#3988AA"
                                            @click="openHistoryDialog(grouped_builds)">
                                            <v-icon small left>mdi-history</v-icon>
                                            View Email History
                                        </v-btn>
                                        <v-btn class="custom-buttons mr-2" outlined color="#3988AA"
                                            @click="openGroupsizeHistoryDialog(grouped_builds)">
                                            <v-icon small left>mdi-account-clock</v-icon>
                                            View Groupsize History
                                        </v-btn>
                                        <!-- This supplier: a template, or a booking change email; untick services in the dialog -->
                                        <SupplierTemplateMenu :templates="email_templates" :loading="loading_templates"
                                            :cancellation-disabled="deletedBuildsFor(grouped_builds[0].supplier_id).length === 0"
                                            availability @availability="openAvailability(grouped_builds)"
                                            @select="openTemplateEmails($event, grouped_builds)"
                                            @change="openChangeEmails($event, grouped_builds)">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn class="custom-buttons mr-2" color="#3988AA" dark
                                                    v-bind="attrs" v-on="on">
                                                    <v-icon small left>mdi-email-fast-outline</v-icon>
                                                    Email Supplier{{ tickedIn(grouped_builds).length ? ` (${tickedIn(grouped_builds).length})` : '' }}
                                                    <v-icon small right>mdi-chevron-down</v-icon>
                                                </v-btn>
                                            </template>
                                        </SupplierTemplateMenu>
                                        <v-btn icon color="#E53935" :disabled="locked" title="Delete services"
                                            @click="openDeleteConfirmationDialog(grouped_builds)">
                                            <v-icon>mdi-delete-outline</v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <v-divider class="my-4" />

                                <div class="builds-table builds-table--selectable">
                                    <div class="builds-table-header">
                                        <div>
                                            <v-checkbox :input-value="grouped_builds.every(build => ticked_build_ids.includes(build.id))"
                                                :indeterminate="tickedIn(grouped_builds).length > 0 && tickedIn(grouped_builds).length < grouped_builds.length"
                                                hide-details dense style="margin-top:-6px;" @click="toggleTickedAll(grouped_builds)" />
                                        </div>
                                        <div>SERVICE</div>
                                        <div>START TIME</div>
                                        <div>DATE</div>
                                        <div>FINAL RATE</div>
                                        <div>GROUPSIZE</div>
                                        <div>ADDITIONAL NOTES</div>
                                        <div>STATUSES</div>
                                    </div>
                                    <div v-for="build in grouped_builds" :key="build.id" class="build-group"
                                        :class="{ 'build-group--shared': hasPackageSplit(build) }">
                                    <div class="builds-table-row" :class="{ 'builds-table-row--shared': hasPackageSplit(build) }">
                                        <!-- Ticked services start ticked in Email Supplier/s and Delete -->
                                        <div class="build-col-checkbox">
                                            <v-checkbox :input-value="ticked_build_ids.includes(build.id)"
                                                @change="toggleTicked(build.id)" dense hide-details />
                                        </div>
                                        <div class="build-col-service">
                                            {{ build.option ? build.option.name : '—' }}
                                            <div v-if="optionTypeNames(build)" class="build-col-service-type">
                                                {{ optionTypeNames(build) }}
                                            </div>
                                            <div v-if="hasPackageSplit(build)" class="package-row-name package-row-name--main">
                                                <div>
                                                    <div class="package-row-label">Main package</div>
                                                    <div>{{ build.quote ? build.quote.name : 'No quote' }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="build-col-time">
                                            <v-text-field v-model="build.start_time" dense outlined hide-details
                                                :disabled="locked" @change="saveBuildStartTime(build)" placeholder="HH:MM"
                                                class="build-input" style="max-width:170px!important;" />
                                        </div>
                                        <div class="build-col-date">
                                            <template v-if="build.type_id == 1">
                                                <div v-for="nightIdx in getAccomNightIndices(build)"
                                                    :key="'date-' + nightIdx" class="mb-1 d-flex align-center"
                                                    style="height: 40px;">
                                                    <span class="night-day-label">{{ getNightLabel(build, nightIdx)
                                                    }}</span>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <span class="night-day-label">
                                                    {{ $date(build.start_date).format('ddd DD/MM/YYYY') || '—' }}
                                                </span>
                                            </template>
                                        </div>
                                        <div class="build-col-rate">
                                            <template v-if="build.type_id == 1">
                                                <div v-for="nightIdx in getAccomNightIndices(build)"
                                                    :key="'rate-' + nightIdx" class="mb-1">
                                                    <v-text-field :value="parsedAccomFinalRates(build)[nightIdx] || 0"
                                                        :disabled="locked" @change="val => updateAccomFinalRate(build, nightIdx, val)"
                                                        dense outlined hide-details type="number" min="0"
                                                        class="build-input" :prefix="get_selected_company.currency" />
                                                </div>
                                            </template>
                                            <template v-else>
                                                <v-text-field v-model.number="build.final_rate"
                                                    :disabled="locked" @change="saveBuildFinalRate(build)" dense outlined hide-details
                                                    type="number" min="0" class="build-input"
                                                    :prefix="get_selected_company.currency"
                                                    :suffix="rateSuffix(build) ? '/group' : ''"
                                                    :title="rateSuffix(build) ? 'Per-group rate: the whole group\'s cost, not per person' : ''" />
                                            </template>
                                        </div>
                                        <!-- Shared by several packages: this row is the main package's own groupsize;
                                             the supplier's figure is the Supplier total row below -->
                                        <div v-if="hasPackageSplit(build)" class="build-col-groupsize">
                                            <template v-if="build.type_id == 1">
                                                <div v-for="nightIdx in getAccomNightIndices(build)"
                                                    :key="'main-gs-' + nightIdx" class="mb-1">
                                                    <v-text-field :value="getDisplayShare(build, getAccomNightDate(build, nightIdx), true)"
                                                        :disabled="locked" @change="val => savePackageShare(build, getAccomNightDate(build, nightIdx), val, true)"
                                                        :suffix="`${getPaidInFullCount(build)} paid`" dense outlined hide-details
                                                        type="number" min="0" class="build-input build-input--package" />
                                                </div>
                                            </template>
                                            <v-text-field v-else :value="getDisplayShare(build, null, true)"
                                                :disabled="locked" @change="val => savePackageShare(build, null, val, true)"
                                                :suffix="`${getPaidInFullCount(build)} paid`" dense outlined hide-details
                                                type="number" min="0" class="build-input build-input--package" />
                                        </div>
                                        <div v-else class="build-col-groupsize">
                                            <template v-if="build.type_id == 1">
                                                <div v-for="nightIdx in getAccomNightIndices(build)"
                                                    :key="'groupsize-' + nightIdx" class="mb-1">
                                                    <v-text-field :value="parsedAccomGroupsize(build)[nightIdx] || 0"
                                                        :disabled="locked" @change="val => updateAccomNight(build, nightIdx, val)" dense
                                                        outlined hide-details type="number" min="0"
                                                        class="build-input" />
                                                </div>
                                            </template>
                                            <template v-else>
                                                <v-text-field v-model.number="build.groupsize"
                                                    :disabled="locked" @change="saveBuildGroupsize(build)" dense outlined hide-details
                                                    type="number" min="0" class="build-input" />
                                            </template>
                                        </div>
                                        <div class="build-col-booking-notes">
                                            <v-textarea v-model="build.booking_notes"
                                                :disabled="locked" @change="saveBuildBookingNotes(build)" dense outlined hide-details
                                                auto-grow rows="3" placeholder="Add additional notes..."
                                                class="build-textarea" />
                                        </div>
                                        <div>
                                            <div class="d-flex align-center">
                                                <v-label
                                                    style="font-weight:600;font-size:14px;margin-right:10px;width:70px;">
                                                    Booking:
                                                </v-label>
                                                <v-select :items="booking_statuses" v-model="build.booking_status_id"
                                                    @change="updateBookingStatus(build)" item-text="status"
                                                    item-value="id" dense outlined hide-details
                                                    :disabled="locked || isStatusLocked(build.booking_status_id)"
                                                    :class="['status-select', getStatusClass(build.booking_status_id)]" />
                                            </div>
                                            <br>
                                            <div class="d-flex align-center">
                                                <v-label
                                                    style="font-weight:600;font-size:14px;margin-right:10px;width:70px;">
                                                    Payment:
                                                </v-label>
                                                <v-select :items="payment_statuses" v-model="build.payment_status_id"
                                                    @change="updatePaymentStatus(build)" item-text="status"
                                                    item-value="id" dense outlined hide-details
                                                    :disabled="locked || isStatusLocked(build.payment_status_id)"
                                                    :class="['payment-status-select', getPaymentStatusClass(build.payment_status_id)]" />
                                            </div>
                                        </div>
                                    </div>
                                    <!-- Shared by several packages (Copy Quote variants): a row per variant
                                         package, then the supplier's total, on the same columns as above -->
                                    <template v-if="hasPackageSplit(build)">
                                        <div v-for="member in build.linked_builds" :key="'pkg-' + build.id + '-' + member.id"
                                            class="builds-table-row package-row">
                                            <div></div>
                                            <div class="package-row-name">
                                                <v-icon small color="#9E9E9E" class="mr-1">mdi-subdirectory-arrow-right</v-icon>
                                                <div>
                                                    <div class="package-row-label">Variant package</div>
                                                    <div>{{ member.quote ? member.quote.name : 'No quote' }}</div>
                                                </div>
                                            </div>
                                            <div></div>
                                            <div class="build-col-date">
                                                <template v-if="build.type_id == 1">
                                                    <div v-for="nightIdx in getAccomNightIndices(build)"
                                                        :key="'pkg-date-' + nightIdx" class="mb-1 d-flex align-center"
                                                        style="height: 40px;">
                                                        <span class="night-day-label">{{ getNightLabel(build, nightIdx) }}</span>
                                                    </div>
                                                </template>
                                                <span v-else class="night-day-label">
                                                    {{ $date(build.start_date).format('ddd DD/MM/YYYY') }}
                                                </span>
                                            </div>
                                            <div></div>
                                            <div class="build-col-groupsize">
                                                <template v-if="build.type_id == 1">
                                                    <div v-for="nightIdx in getAccomNightIndices(build)"
                                                        :key="'pkg-gs-' + nightIdx" class="mb-1">
                                                        <v-text-field v-if="staysOnNight(member, getAccomNightDate(build, nightIdx))"
                                                            :value="getDisplayShare(member, getAccomNightDate(build, nightIdx), false)"
                                                            :disabled="locked" @change="val => savePackageShare(member, getAccomNightDate(build, nightIdx), val, false)"
                                                            :suffix="`${getPaidInFullCount(member)} paid`" dense outlined hide-details
                                                            type="number" min="0" class="build-input build-input--package" />
                                                        <div v-else class="package-not-staying">Not staying</div>
                                                    </div>
                                                </template>
                                                <v-text-field v-else :value="getDisplayShare(member, null, false)"
                                                    :disabled="locked" @change="val => savePackageShare(member, null, val, false)"
                                                    :suffix="`${getPaidInFullCount(member)} paid`" dense outlined hide-details
                                                    type="number" min="0" class="build-input build-input--package" />
                                            </div>
                                            <div></div>
                                            <div></div>
                                        </div>
                                        <div :key="'total-' + build.id" class="package-total-row">
                                            <div class="package-total-label">
                                                <span class="package-row-label">Supplier total</span>
                                                <span class="package-total-hint">Sent to the supplier</span>
                                            </div>
                                            <div class="package-total-chips">
                                                <template v-if="build.type_id == 1">
                                                    <span v-for="nightIdx in getAccomNightIndices(build)"
                                                        :key="'total-' + nightIdx" class="package-total-chip">
                                                        <span class="package-total-chip-date">{{ getNightLabel(build, nightIdx) }}</span>
                                                        <b>{{ parsedAccomGroupsize(build)[nightIdx] || 0 }}</b>
                                                    </span>
                                                </template>
                                                <span v-else class="package-total-chip">
                                                    <span class="package-total-chip-date">{{ $date(build.start_date).format('ddd DD/MM/YYYY') }}</span>
                                                    <b>{{ build.groupsize || 0 }}</b>
                                                </span>
                                            </div>
                                        </div>
                                    </template>
                                    </div>
                                </div>
                            </div>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
        <v-row v-if="selected_supplier">
            <v-dialog v-model="history_dialog" max-width="1000" persistent>
                <v-card class="history-dialog-card">
                    <div class="booking-dialog-header pa-5">
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="booking-dialog-title">
                                    <v-icon class="mr-2" color="white" size="20">mdi-history</v-icon>
                                    Email History
                                </div>
                                <div class="booking-dialog-subtitle mt-1">
                                    <v-icon size="14" class="mr-1" color="rgba(255,255,255,0.75)">mdi-domain</v-icon>
                                    {{ selected_supplier.supplier_name }}
                                </div>
                            </div>
                            <v-btn icon dark @click="closeHistoryDialog()">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <v-card-text class="pa-6">
                        <div v-if="history_loading" class="history-empty py-10">
                            <v-progress-circular indeterminate color="#3988AA" size="48" />
                            <div class="mt-3">Loading email history...</div>
                        </div>
                        <div v-else-if="booking_history.length === 0" class="history-empty py-10">
                            <v-icon size="48" color="#D0D0D0">mdi-email-outline</v-icon>
                            <div class="mt-3">No emails have been sent to this supplier yet.</div>
                        </div>
                        <div v-else class="history-list">
                            <div v-for="(item, index) in booking_history" :key="index" class="history-item mb-4">
                                <div class="history-item-header d-flex align-center justify-space-between px-4 py-3">
                                    <div>
                                        <div class="d-flex align-center">
                                            <v-icon size="16" color="#3988AA" class="mr-2">mdi-email-outline</v-icon>
                                            <span class="history-subject">{{ item.subject }}</span>
                                        </div>
                                        <div v-if="item.to_email" class="d-flex align-center mt-1">
                                            <v-icon size="13" color="#AAAAAA" class="mr-1">mdi-at</v-icon>
                                            <span class="history-meta">{{ item.to_email }}</span>
                                        </div>
                                    </div>
                                    <div class="d-flex align-center history-meta">
                                        <v-icon size="13" color="#AAAAAA" class="mr-1">mdi-account-outline</v-icon>
                                        <span class="mr-4">{{ item.sender?.name }}</span>
                                        <v-icon size="13" color="#AAAAAA" class="mr-1">mdi-clock-outline</v-icon>
                                        <span>{{ $date(item.created_at).format('DD MMM YYYY [at] h:mma') }}</span>
                                    </div>
                                </div>
                                <div class="history-item-body px-4 py-3">
                                    <div v-html="item.body" class="history-body-content"></div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>

                    <v-divider />
                    <v-card-actions class="pa-4">
                        <v-spacer />
                        <v-btn text class="custom-buttons" @click="closeHistoryDialog()">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row v-if="supplier_to_send_to">
            <v-dialog v-model="send_email_dialog" max-width="600" persistent>
                <v-card>
                    <v-card-title>Are you sure you want to send a groupsize update email?</v-card-title>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn class="custom-buttons" @click="closeSendEmailDialog()">Cancel</v-btn>
                        <v-btn class="custom-buttons" dark
                            @click="updateQuoteBuildAndSend(supplier_to_send_to)">Send</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row v-if="supplier_to_send_to">
            <v-dialog v-model="send_email_dialog_accom" max-width="600" persistent>
                <v-card>
                    <v-card-title>Are you sure you want to send a groupsize update email?</v-card-title>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn class="custom-buttons" @click="closeSendEmailAccomDialog()">Cancel</v-btn>
                        <v-btn class="custom-buttons" dark
                            @click="updateQuoteBuildAccomAndSend(supplier_to_send_to, groupsize_to_send)">Send</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row>
            <v-dialog v-model="update_groupsize_dialog" max-width="600" persistent>
                <v-card>
                    <v-card-title>Are you sure you want to update final groupsizes?</v-card-title>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn class="custom-buttons" @click="closeUpdateGroupsizeDialog()">Cancel</v-btn>
                        <v-btn class="custom-buttons" dark @click="updateGroupsizes()">Send</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row>
            <v-dialog v-model="final_groupsize_dialog" max-width="600px" persistent>
                <v-card>
                    <v-card-title class="headline">Update Final Groupsizes</v-card-title>
                    <v-card-text>
                        <p class="mb-0">This will update all groupsizes with the final numbers of invitees paid as shown
                            in the
                            Attendance table. Do you want to proceed?</p>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey" text @click="closeFinalGroupsizeDialog">Cancel</v-btn>
                        <v-btn color="#3988AA" dark @click="updateFinalGroupsizes">Confirm</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <!-- Email Supplier/s (templates and booking changes) and final groupsizes: one previewed email per supplier -->
        <SupplierEmailsDialog :show="manual_email.show" :title="manual_email.title" :subtitle="manual_email.subtitle"
            :email_type="manual_email.email_type" :items="manual_email.items" :event="event" :note="manual_email.note"
            :sent_services="sent_services" @close="closeManualEmail()" @sent="onManualEmailSent()" />
        <!-- After deleting booked services: offer the suppliers a cancellation email -->
        <SupplierChangesPrompt :changes="supplier_changes" :event="event" @close="supplier_changes = null"
            @sent="onManualEmailSent()" />
        <v-row v-if="selected_supplier">
            <v-dialog v-model="groupsize_history_dialog" max-width="900" persistent>
                <v-card class="history-dialog-card">
                    <div class="booking-dialog-header pa-5">
                        <div class="d-flex align-center justify-space-between">
                            <div>
                                <div class="booking-dialog-title">
                                    <v-icon class="mr-2" color="white" size="20">mdi-account-clock</v-icon>
                                    Groupsize History
                                </div>
                                <div class="booking-dialog-subtitle mt-1">
                                    <v-icon size="14" class="mr-1" color="rgba(255,255,255,0.75)">mdi-domain</v-icon>
                                    {{ selected_supplier.supplier_name }}
                                </div>
                            </div>
                            <v-btn icon dark @click="closeGroupsizeHistoryDialog()">
                                <v-icon>mdi-close</v-icon>
                            </v-btn>
                        </div>
                    </div>

                    <v-card-text class="pa-6">
                        <div v-if="groupsize_history_list.length === 0" class="history-empty py-10">
                            <v-icon size="48" color="#D0D0D0">mdi-account-clock</v-icon>
                            <div class="mt-3">No groupsize changes recorded for these services yet.</div>
                        </div>
                        <div v-else class="history-list">
                            <div v-for="(group, groupIndex) in groupsize_history_list" :key="groupIndex" class="mb-5">
                                <!-- Service Name Header -->
                                <div class="d-flex align-center px-4 py-2"
                                    style="background-color: #f5f5f5; border-left: 4px solid #3988AA;">
                                    <v-icon size="18" color="#3988AA" class="mr-2">mdi-package-variant</v-icon>
                                    <span class="font-weight-bold" style="font-size: 15px; color: #333;">{{
                                        group.service_name
                                    }}</span>
                                </div>

                                <!-- History Records for this Service -->
                                <div v-for="(record, recordIndex) in group.history" :key="recordIndex"
                                    class="history-item mb-3 ml-4">
                                    <div
                                        class="history-item-header d-flex align-center justify-space-between px-4 py-2">
                                        <div class="d-flex align-center history-meta">
                                            <v-icon size="13" color="#AAAAAA" class="mr-1">mdi-clock-outline</v-icon>
                                            <span>{{ $date(record.created_at).format('DD MMM YYYY [at] h:mma') }}</span>
                                        </div>
                                        <div class="d-flex align-center" style="gap:6px;">
                                            <span v-if="record.is_first" class="history-tag">First recorded</span>
                                            <span v-for="name in record.changed_packages" :key="name"
                                                class="history-tag history-tag--package">
                                                <v-icon x-small color="#B26A00" class="mr-1">mdi-pencil</v-icon>{{ name }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="history-item-body px-4 py-3">
                                        <!-- Supplier total: what the supplier is sent -->
                                        <div class="history-section-title">
                                            {{ record.packages ? 'Supplier total' : (record.is_accommodation ? 'Accommodation Groupsize' : 'Groupsize') }}
                                        </div>
                                        <div class="history-values">
                                            <div v-for="row in record.total" :key="row.label" class="history-value-row">
                                                <span class="history-value-label">{{ row.label }}</span>
                                                <span :class="{ 'history-value--changed': row.was !== undefined }">{{ row.value ?? '–' }} pax</span>
                                                <span v-if="row.was !== undefined" class="history-change"
                                                    :class="row.direction ? 'history-change--' + row.direction : ''">
                                                    <v-icon v-if="row.direction" x-small>{{ row.direction === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                                                    was {{ row.was ?? '–' }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Shared by several packages: each package's own numbers -->
                                        <div v-if="record.packages" class="history-packages">
                                            <div v-for="pkg in record.packages" :key="pkg.name + pkg.is_main"
                                                class="history-package" :class="{ 'history-package--changed': pkg.changed }">
                                                <div class="package-row-label">{{ pkg.is_main ? 'Main package' : 'Variant package' }}</div>
                                                <div class="history-package-name">{{ pkg.name }}</div>
                                                <div v-for="row in pkg.rows" :key="row.label" class="history-value-row">
                                                    <span class="history-value-label">{{ row.label }}</span>
                                                    <span v-if="row.not_staying" class="package-not-staying-inline">Not staying</span>
                                                    <template v-else>
                                                        <span :class="{ 'history-value--changed': row.was !== undefined }">
                                                            {{ row.value === null ? 'Not entered' : row.value + ' pax' }}
                                                        </span>
                                                        <span v-if="row.was !== undefined" class="history-change"
                                                            :class="row.direction ? 'history-change--' + row.direction : ''">
                                                            <v-icon v-if="row.direction" x-small>{{ row.direction === 'up' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                                                            was {{ row.was === null ? 'not entered' : row.was }}
                                                        </span>
                                                    </template>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>

                    <v-divider />
                    <v-card-actions class="pa-4">
                        <v-spacer />
                        <v-btn text class="custom-buttons" @click="closeGroupsizeHistoryDialog()">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row>
            <v-dialog v-model="delete_confirmation_dialog" max-width="500" persistent>
                <v-card class="booking-dialog-card">
                    <v-card-title class="booking-dialog-header white--text pa-4">
                        <v-icon color="white" class="mr-2">mdi-alert</v-icon>
                        <span class="booking-dialog-title">Confirm Deletion</span>
                    </v-card-title>

                    <v-card-text class="pa-6">
                        <div style="font-family: Manrope; font-size: 14px; color: #333;">
                            Tick the services to delete. This can be undone by restoring them from the deleted
                            services view.
                        </div>
                        <div class="mt-4">
                            <strong style="font-family: Manrope; font-size: 13px; color: #7C7C7C;">Services to delete ({{
                                selected_build_ids.size }} of {{ delete_builds.length }}):</strong>
                            <div class="selected-builds-list mt-2">
                                <div v-for="build in delete_builds" :key="build.id" class="selected-build-item">
                                    <v-checkbox :input-value="selected_build_ids.has(build.id)" hide-details dense
                                        class="mt-0 pt-0 mr-2" @change="toggleDeleteBuild(build.id)" />
                                    <div style="flex:1;min-width:0;">
                                        <span class="selected-build-name">{{ build.option ? build.option.name : 'N/A'
                                        }}</span>
                                        <div class="selected-build-meta">
                                            <span>
                                                <v-icon x-small>mdi-calendar</v-icon>
                                                {{ $date(build.start_date).format('ddd DD/MM/YYYY') }}
                                            </span>
                                            <span v-if="build.supplier"><v-icon x-small>mdi-office-building</v-icon>{{
                                                build.supplier.supplier_name }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="selectedLinkedBuilds.length > 0" class="mt-4"
                            style="font-family: Manrope; font-size: 13px; color: #333;">
                            <strong>Also booked on {{ selectedLinkedQuoteNames.join(', ') }}.</strong>
                            Remove it there too?
                            <v-radio-group v-model="delete_linked_choice" hide-details dense class="mt-2">
                                <v-radio value="all"
                                    :label="`Yes, remove from ${selectedLinkedQuoteNames.length > 1 ? 'those quotes' : 'that quote'} as well`" />
                                <v-radio value="this"
                                    :label="`No, keep it on ${selectedLinkedQuoteNames.length > 1 ? 'those quotes' : 'that quote'} (it will show here in its place)`" />
                            </v-radio-group>
                        </div>
                    </v-card-text>

                    <v-divider />
                    <v-card-actions class="pa-4">
                        <v-spacer />
                        <v-btn text class="custom-buttons" @click="closeDeleteConfirmationDialog()">Cancel</v-btn>
                        <v-btn color="#E53935" class="custom-buttons white--text" @click="confirmDeleteBuilds()"
                            :disabled="selected_build_ids.size === 0 || (selectedLinkedBuilds.length > 0 && !delete_linked_choice)">
                            <v-icon small left>mdi-delete</v-icon>
                            Delete
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row>
            <v-dialog v-model="edit_supplier_email_dialog" max-width="500" persistent>
                <v-card class="booking-dialog-card">
                    <v-card-title class="booking-dialog-header white--text pa-4">
                        <v-icon color="white" class="mr-2">mdi-pencil-outline</v-icon>
                        <span class="booking-dialog-title">Edit Supplier Email</span>
                    </v-card-title>

                    <v-card-text class="pa-6">
                        <div class="booking-section-header mb-1">
                            EMAIL
                        </div>
                        <v-text-field v-model="edit_supplier_email" outlined dense
                            placeholder="Enter supplier email" clearable hide-details />
                        <div class="mt-3" style="font-family: Manrope; font-size: 13px; color: #7C7C7C;">
                            Please confirm that the entered supplier email is correct
                        </div>
                    </v-card-text>

                    <v-divider />
                    <v-card-actions class="pa-4">
                        <v-spacer />
                        <v-btn text class="custom-buttons" @click="closeEditSupplierEmailDialog()">Cancel</v-btn>
                        <v-btn color="#3988AA" dark class="custom-buttons" @click="confirmEditSupplierEmail()">
                            <v-icon small left>mdi-check</v-icon>
                            Confirm
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row>
            <v-dialog v-model="view_deleted_dialog" max-width="1400" persistent>
                <v-card class="booking-dialog-card">
                    <v-card-title class="booking-dialog-header white--text pa-4">
                        <v-icon color="white" class="mr-2">mdi-delete-restore</v-icon>
                        <span class="booking-dialog-title">Deleted Services</span>
                    </v-card-title>

                    <v-card-text class="pa-4" style="max-height: 70vh; overflow-y: auto;">
                        <div v-if="deleted_builds.length === 0" class="history-empty py-10">
                            <v-icon size="60" color="#CCCCCC">mdi-delete-empty</v-icon>
                            <div class="mt-4">No deleted services</div>
                        </div>
                        <div v-else>
                            <v-expansion-panels v-model="deleted_panels_open" multiple class="custom-expansion-panels">
                                <v-expansion-panel v-for="(grouped_builds, index) in getDeletedBuildsBySupplier()"
                                    :key="grouped_builds[0].supplier_id ?? index" class="custom-expansion-panel mb-3">
                                    <v-expansion-panel-header class="custom-expansion-header">
                                        <div
                                            style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                                            <div style="display: flex; align-items: center;">
                                                <v-icon color="white" class="mr-2">mdi-office-building</v-icon>
                                                {{ grouped_builds[0].supplier ? grouped_builds[0].supplier.supplier_name
                                                    :
                                                    'Unknown Supplier' }}
                                            </div>
                                            <div style="font-size: 12px; font-weight: 500;">
                                                {{ grouped_builds.length }} service{{ grouped_builds.length !== 1 ? 's'
                                                    : '' }}
                                            </div>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content class="custom-expansion-panel-content">
                                        <div class="supplier-panel-body">
                                            <div class="builds-table builds-table--selectable">
                                                <div class="builds-table-header">
                                                    <div>
                                                        <v-checkbox
                                                            :input-value="grouped_builds.every(build => selected_deleted_build_ids.has(build.id))"
                                                            :indeterminate="grouped_builds.some(build => selected_deleted_build_ids.has(build.id)) && !grouped_builds.every(build => selected_deleted_build_ids.has(build.id))"
                                                            hide-details dense style="margin-top:-6px;"
                                                            @click="toggleSelectAllDeletedBuilds(grouped_builds)" />
                                                    </div>
                                                    <div>SERVICE</div>
                                                    <div>START TIME</div>
                                                    <div>DATE</div>
                                                    <div>FINAL RATE</div>
                                                    <div>GROUPSIZE</div>
                                                    <div>ADDITIONAL NOTES</div>
                                                    <div>STATUSES</div>
                                                </div>
                                                <div v-for="build in grouped_builds" :key="build.id"
                                                    class="builds-table-row">
                                                    <div class="build-col-checkbox">
                                                        <v-checkbox
                                                            :input-value="selected_deleted_build_ids.has(build.id)"
                                                            hide-details dense
                                                            @click="toggleDeletedBuildSelection(build.id)" />
                                                    </div>
                                                    <div class="build-col-service">
                                                        {{ build.option ? build.option.name : 'N/A' }}
                                                        <div v-if="optionTypeNames(build)" class="build-col-service-type">
                                                            {{ optionTypeNames(build) }}
                                                        </div>
                                                    </div>
                                                    <div class="build-col-time">
                                                        {{ build.start_time || 'N/A' }}
                                                    </div>
                                                    <div class="build-col-date">
                                                        <div>{{ $date(build.start_date).format('ddd DD/MM/YYYY') }}</div>
                                                    </div>
                                                    <div class="build-col-rate">
                                                        {{ get_selected_company.currency }}{{ build.final_rate ||
                                                            defaultFinalRate(build) ||
                                                            '0' }}{{ rateSuffix(build) }}
                                                    </div>
                                                    <div>
                                                        <div v-if="build.type_id == 1">
                                                            <div
                                                                v-if="build.accom_groupsize && build.accom_groupsize.length > 0">
                                                                <div v-for="(size, idx) in build.accom_groupsize"
                                                                    :key="idx"
                                                                    style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                                                                    <span class="night-day-label">{{
                                                                        getNightLabel(build, idx)
                                                                    }}</span>
                                                                    <span
                                                                        style="font-family: Manrope; font-size: 13px; color: #101010;">{{
                                                                            size }}</span>
                                                                </div>
                                                            </div>
                                                            <div v-else
                                                                style="font-family: Manrope; font-size: 13px; color: #AAAAAA;">
                                                                N/A
                                                            </div>
                                                        </div>
                                                        <div v-else>
                                                            {{ build.groupsize || 'N/A' }}
                                                        </div>
                                                    </div>
                                                    <div class="build-col-booking-notes">
                                                        {{ build.booking_notes || 'N/A' }}
                                                    </div>
                                                    <div>
                                                        <div class="mb-1">
                                                            <span class="status-pill"
                                                                :class="getStatusClass(build.booking_status_id)">
                                                                {{ build.booking_status ? build.booking_status.status :
                                                                    'Unknown' }}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span class="status-pill"
                                                                :class="getPaymentStatusClass(build.payment_status_id)">
                                                                {{ build.payment_status ? build.payment_status.status :
                                                                    'Unknown' }}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </div>
                    </v-card-text>

                    <v-divider />
                    <v-card-actions class="pa-4">
                        <v-btn v-if="selected_deleted_build_ids.size > 0" color="#27AE60" :dark="!locked"
                            :disabled="locked" class="custom-buttons" @click="restoreSelectedBuilds()">
                            <v-icon small left>mdi-restore</v-icon>
                            Restore Selected Services ({{ selected_deleted_build_ids.size }})
                        </v-btn>
                        <v-spacer />
                        <v-btn text class="custom-buttons" @click="closeViewDeletedDialog()">Close</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-row>
        <v-row v-if="event && event.lead">
            <v-col cols="12">
                <SupplierAvailability ref="availability" :lead="event.lead" :builds="combined_builds"
                    :location-name="event.lead.location ? event.lead.location.name : 'this location'" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import { mapGetters } from "vuex"
import _ from 'lodash'
import { groupLinkedBuilds } from '@/utils/linkedBuilds'
import { defaultFinalRate, rateSuffix } from '@/utils/finalRates'
import { detailsAssignedTo } from '@/utils/eventPayments'
import { changeEmailDialog, deletedBuildGroups } from '@/utils/supplierChangeEmails'
import { loadEmailTemplates, loadSentServices, templateEmailItems, templateEmailNote } from '@/utils/supplierTemplateEmails'
import SupplierEmailsDialog from './components/SupplierEmailsDialog.vue'
import SupplierTemplateMenu from './components/SupplierTemplateMenu.vue'
import SupplierChangesPrompt from './components/SupplierChangesPrompt.vue'
import SupplierAvailability from '../suppliers/availability/SupplierAvailability.vue'
import { availabilityPreset } from '@/utils/supplierAvailability'
import { isBookingClosed } from '@/utils/eventStatus'

// Matches the quote builder's wizard step order (CustomQuote.vue's build_index_supplier_types)
// so same-day services appear in the same order the customer built them in. Accommodation
// (type_id 1) is pinned first wherever this is used as a sort key, since it spans the whole
// party rather than sitting on one day.
const BUILD_TYPE_ORDER = [1, 2, 6, 8, 4, 5, 3, 7]
const accommodationFirst = (build) => build.type_id === 1 ? 0 : 1

export default {
    components: {
        SupplierEmailsDialog, SupplierChangesPrompt, SupplierAvailability, SupplierTemplateMenu,
    },
    data: () => ({
        page_name: "Suppliers",
        classes: {
        },
        selected_supplier: null,
        edit_supplier_email_dialog: false,
        editing_supplier: null,
        edit_supplier_email: '',
        history_dialog: false,
        history_loading: false,
        booking_history: [],
        groupsize_history_dialog: false,
        groupsize_history_list: [],
        history_headers: [
            { text: 'Subject', sortable: false, value: 'subject' },
            { text: 'Body', sortable: false, value: 'body' },
            { text: 'Date Sent', sortable: false, value: 'created_at' },
            { text: 'Sent By', sortable: false, value: 'sent_by' },
        ],
        sort_by: 'created_at',
        sort_desc: true,
        // The company's email templates, for the Email Supplier/s dropdowns.
        email_templates: null,
        loading_templates: false,
        // Which services each supplier was already emailed about (get_sent_services), loaded with
        // the tab so the email dialog shows its "Sent" tags at once. Null until loaded.
        sent_services: null,
        menu: [],
        build_headers: [
            { text: 'Service', sortable: false },
            { text: 'Rate', sortable: false },
            { text: 'Day/s', sortable: false },
            { text: 'Time', sortable: false },
            { text: 'Groupsize', sortable: false },
        ],
        open_panels: [],
        days_of_week: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        send_email_dialog_accom: false,
        send_email_dialog: false,
        supplier_to_send_to: null,
        groupsize_to_send: null,
        update_groupsize_dialog: false,
        // The delete dialog: one supplier's services, and the ones ticked to delete.
        delete_builds: [],
        // Services ticked on the page (any suppliers): Email Supplier/s and Delete start with them.
        ticked_build_ids: [],
        selected_build_ids: new Set(),
        final_groupsize_dialog: false,
        loading_groupsize_emails: false,
        // SupplierEmailsDialog for Email Supplier/s and final groupsizes.
        manual_email: {
            show: false,
            title: '',
            subtitle: '',
            email_type: null,
            items: [],
            note: '',
        },
        // From the API after deleting services: booked suppliers to offer a cancellation email.
        supplier_changes: null,
        booking_statuses: [
            { id: 1, status: 'Unconfirmed' },
            { id: 2, status: 'Requested' },
            { id: 3, status: 'Confirmed' },
            { id: 4, status: 'Cancelled' },
            { id: 5, status: 'Cancellation Pending' },
        ],
        payment_statuses: [
            { id: 1, status: 'Notification Not Sent' },
            { id: 2, status: 'Notification Sent' },
            { id: 3, status: 'Payment Received' },
            { id: 4, status: 'Payment Not Received' },
        ],
        delete_confirmation_dialog: false,
        delete_linked_choice: null,
        show_package_split: false,
        deleted_builds: [],
        view_deleted_dialog: false,
        selected_deleted_build_ids: new Set(),
        deleted_panels_open: [],
        start_from_event_date: false,
        loading_deleted_builds: true,
    }),
    created() {
    },
    async mounted() {
        this.loadTemplates()
        await this.initializeFinalRates()
        await this.loadDeletedBuilds()
        this.loadSentServices()
        await this.$store.dispatch('supplier/fetch_supplier_type')
    },
    watch: {
        builds: {
            immediate: true,
            handler(val) {
                this.open_panels = Object.keys(val).map((_, i) => i)
            }
        }
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
            get_selected_company: 'auth/get_selected_company',
            event: 'eventsStore/getSelectedEventGetter',
            get_supplier_type: 'supplier/get_supplier_type',
        }),
        // Booking Closed locks the booking details (times, rates, groupsizes, notes, booking and
        // payment statuses, deleting and restoring services). Emails and Update Final Groupsizes
        // stay usable: they're the close-off work done while Booking Closed.
        locked() {
            return isBookingClosed(this.event)
        },
        // Every build on the event's quotes, including copies hidden by combined_builds.
        all_builds() {
            let all_builds = new Array()
            this.event.quotes.forEach(quote => {
                // Add quote reference to each build
                const buildsWithQuote = quote.builds.map(build => ({
                    ...build,
                    quote: quote
                }))
                all_builds = all_builds.concat(buildsWithQuote)
            })
            // Order the same way as ManageItinerary/Review so services appear in a consistent order everywhere
            return _.orderBy(all_builds, [accommodationFirst, 'start_date', (build) => BUILD_TYPE_ORDER.indexOf(build.type_id)], ['asc', 'asc', 'asc'])
        },
        // One build per supplier booking: copies of a build on variant quotes are folded into it
        // (see utils/linkedBuilds) and kept on build.linked_builds for groupsizes and deletes.
        combined_builds() {
            return groupLinkedBuilds(this.all_builds).map(({ build, linked }) => {
                build.linked_builds = linked
                return build
            })
        },
        builds() {
            const combined_builds = this.combined_builds
            const grouped = _.groupBy(combined_builds, 'supplier_id')
            const orderedSupplierIds = []
            combined_builds.forEach(build => {
                if (!orderedSupplierIds.includes(build.supplier_id)) {
                    orderedSupplierIds.push(build.supplier_id)
                }
            })
            return orderedSupplierIds
                .map(supplierId => grouped[supplierId])
                .filter(builds => builds && builds.length > 0)
        },
        total() {
            let total = 0
            let combined_builds = new Array()
            this.event.quotes.forEach(quote => {
                combined_builds = combined_builds.concat(quote.builds)
            })
            combined_builds.forEach(build => {
                let attendees = this.getPaidInFullCount(build)
                if (build.type_id == 1) {
                    total += build.rate * parseInt(attendees) * (this.$date(build.end_date).diff(this.$date(build.start_date), 'day'))
                }
                else {
                    total += build.rate * parseInt(attendees)
                }
            })
            return total
        },
        // Hidden copies (on variant quotes) of the services selected for deletion.
        selectedLinkedBuilds() {
            return this.combined_builds
                .filter(build => this.selected_build_ids.has(build.id))
                .flatMap(build => build.linked_builds || [])
        },
        selectedLinkedQuoteNames() {
            return _.uniq(this.selectedLinkedBuilds.map(build => build.quote ? build.quote.name : 'another quote'))
        },
        hasConfirmedBuilds() {
            return this.combined_builds.some(build => build.booking_status_id === 3)
        },
        attendance_headers() {
            const headers = [{ text: 'Supplier', sortable: false }]

            // Get the event start date
            const startDate = this.event?.start_date || this.event?.lead?.start_date

            if (!startDate) {
                // Fallback to static headers if no start date
                return [
                    { text: 'Supplier', sortable: false },
                    { text: 'Sun', sortable: false, width: '110px' },
                    { text: 'Mon', sortable: false, width: '110px' },
                    { text: 'Tue', sortable: false, width: '110px' },
                    { text: 'Wed', sortable: false, width: '110px' },
                    { text: 'Thu', sortable: false, width: '110px' },
                    { text: 'Fri', sortable: false, width: '110px' },
                    { text: 'Sat', sortable: false, width: '110px' },
                ]
            }

            if (this.start_from_event_date) {
                // Generate headers for 7 days starting from the event start date
                for (let i = 0; i < 7; i++) {
                    const currentDate = this.$date(startDate).add(i, 'day')
                    const dayShort = currentDate.format('ddd')
                    const dateFormatted = currentDate.format('MMM D')
                    headers.push({
                        text: `${dayShort} (${dateFormatted})`,
                        sortable: false,
                        width: '110px'
                    })
                }
            } else {
                // Generate headers for Sun-Sat with dates wrapping around from event date
                const eventDate = this.$date(startDate)
                const eventDayOfWeek = eventDate.day() // 0 = Sunday, 6 = Saturday

                for (let i = 0; i < 7; i++) {
                    // Calculate offset: days from Sunday (i) are either same week or next week
                    let offset
                    if (i >= eventDayOfWeek) {
                        offset = i - eventDayOfWeek // Same week
                    } else {
                        offset = i - eventDayOfWeek + 7 // Next week (wrapped)
                    }

                    const currentDate = eventDate.add(offset, 'day')
                    const dayShort = currentDate.format('ddd')
                    const dateFormatted = currentDate.format('MMM D')
                    headers.push({
                        text: `${dayShort} (${dateFormatted})`,
                        sortable: false,
                        width: '110px'
                    })
                }
            }

            headers.push({ text: 'Quoted Rate', sortable: false, width: '120px' })  // Add a subtotal column at the end
            headers.push({ text: 'Total Paid', sortable: false, width: '120px' })  // Add a total column at the end

            return headers
        },
        eventDaysOfWeek() {
            const startDate = this.event?.start_date || this.event?.lead?.start_date
            if (!startDate) {
                // Fallback to static days
                return this.days_of_week
            }

            const days = []
            if (this.start_from_event_date) {
                // Generate days starting from event start date
                for (let i = 0; i < 7; i++) {
                    const currentDate = this.$date(startDate).add(i, 'day')
                    days.push(currentDate.format('dddd'))
                }
            } else {
                // Generate days starting from Sunday with dates wrapping around from event date
                const eventDate = this.$date(startDate)
                const eventDayOfWeek = eventDate.day() // 0 = Sunday, 6 = Saturday

                for (let i = 0; i < 7; i++) {
                    // Calculate offset: days from Sunday (i) are either same week or next week
                    let offset
                    if (i >= eventDayOfWeek) {
                        offset = i - eventDayOfWeek // Same week
                    } else {
                        offset = i - eventDayOfWeek + 7 // Next week (wrapped)
                    }

                    const currentDate = eventDate.add(offset, 'day')
                    days.push(currentDate.format('dddd'))
                }
            }
            return days
        }
    },
    methods: {
        defaultFinalRate,
        rateSuffix,
        getQuoteForBuild(build) {
            // Find which quote this build belongs to
            for (const quote of this.event.quotes) {
                if (quote.builds.some(b => b.id === build.id)) {
                    return quote
                }
            }
            return null
        },
        // The quote the organiser / an invitee paid in full for. Matched on id, not name: a copied
        // quote can share its original's name.
        getOrganizerPackageQuoteId() {
            const detail = detailsAssignedTo(this.event, this.event.lead.id).find(y => y.payment_type_id == 1)
            return detail?.quote_id || null
        },
        // Matched by paid_to (who the Full Payment is for), whoever paid it.
        getInviteePackageQuoteId(item) {
            const detail = detailsAssignedTo(this.event, item.id).find(y => y.payment_type_id == 1)
            return detail?.quote_id || null
        },
        getPaidInFullCount(build) {
            const quote = this.getQuoteForBuild(build)
            if (!quote) return 0

            let count = 0

            // Check if lead has paid in full for this quote
            const leadQuoteId = this.getOrganizerPackageQuoteId()
            if (leadQuoteId && leadQuoteId == quote.id) {
                count++
            }

            // Check each invitee
            this.event.invitees.forEach(invitee => {
                const inviteeQuoteId = this.getInviteePackageQuoteId(invitee)
                if (inviteeQuoteId && inviteeQuoteId == quote.id) {
                    count++
                }
            })

            return count
        },
        // Paid in full guests for a shown build's supplier booking: its own quote's guests plus
        // those of the variant quotes folded into it. For accommodation, pass the night's date to
        // count only the stays that include that night (e.g. a 1 night variant of a 2 night stay).
        getBookingPaidCount(build, night = null) {
            return [build, ...(build.linked_builds || [])]
                .filter(member => night === null || this.staysOnNight(member, night))
                .reduce((sum, member) => sum + this.getPaidInFullCount(member), 0)
        },
        staysOnNight(build, night) {
            const date = this.$date(night).format('YYYY-MM-DD')
            const start = this.$date(build.start_date).format('YYYY-MM-DD')
            const end = this.$date(build.end_date).format('YYYY-MM-DD')
            return date >= start && (date < end || (end <= start && date === start))
        },
        getAccomNightDate(build, nightIdx) {
            return this.$date(build.start_date).add(nightIdx, 'day')
        },
        getAttendanceOnDay(build, dayName) {
            if (!this.hasAttendanceOnDay(build, dayName)) return 0
            if (build.type_id == 1) {
                return this.getAccomNightIndices(build)
                    .map(i => this.getAccomNightDate(build, i))
                    .filter(night => night.format('dddd') === dayName)
                    .reduce((sum, night) => sum + this.getBookingPaidCount(build, night), 0)
            }
            return this.getBookingPaidCount(build)
        },
        // Takings for the row, each quote's guests at that quote's own rate and nights.
        getAttendanceSubtotal(build) {
            return [build, ...(build.linked_builds || [])].reduce((sum, member) => {
                const nights = member.type_id == 1 ? this.$date(member.end_date).diff(this.$date(member.start_date), 'day') : 1
                return sum + this.getPaidInFullCount(member) * member.rate * nights
            }, 0)
        },
        // A shown build plus the copies of it on variant quotes, one per package.
        packageMembers(build) {
            return [build, ...(build.linked_builds || [])]
        },
        hasPackageSplit(build) {
            return (build.linked_builds || []).length > 0
        },
        // One package's paid guests on a weekday, for the Attendance sub-rows.
        getMemberAttendanceOnDay(member, dayName) {
            if (!this.hasAttendanceOnDay(member, dayName)) return 0
            const paid = this.getPaidInFullCount(member)
            if (member.type_id == 1) {
                return this.getAccomNightIndices(member)
                    .filter(i => this.getAccomNightDate(member, i).format('dddd') === dayName).length * paid
            }
            return paid
        },
        getMemberSubtotal(member) {
            const nights = member.type_id == 1 ? this.$date(member.end_date).diff(this.$date(member.start_date), 'day') : 1
            return this.getPaidInFullCount(member) * member.rate * nights
        },
        // A package's own groupsize on a shared build (package_groupsize, or package_accom_groupsize
        // per night of that package's stay). null until it's filled in.
        packageAccomShares(member) {
            const shares = member.package_accom_groupsize
            if (Array.isArray(shares)) return shares
            if (typeof shares === 'string') {
                try {
                    const parsed = JSON.parse(shares)
                    return Array.isArray(parsed) ? parsed : []
                } catch (e) {
                    return []
                }
            }
            return []
        },
        getPackageShare(member, night) {
            if (night === null) return member.package_groupsize ?? null
            const index = this.$date(night).diff(this.$date(member.start_date).startOf('day'), 'day')
            const share = this.packageAccomShares(member)[index]
            return share === undefined || share === '' ? null : share
        },
        // The number shown in a package's groupsize input. Not entered yet: the main package's
        // falls back to the supplier groupsize it had before it was shared, a variant's to 0 (the
        // API seeds them the same way on the first edit, so the total doesn't jump).
        getDisplayShare(member, night, isMain) {
            const share = this.getPackageShare(member, night)
            if (share !== null) return share
            if (!isMain) return 0
            if (night === null) return member.groupsize || 0
            const index = this.$date(night).diff(this.$date(member.start_date).startOf('day'), 'day')
            return this.parsedAccomGroupsize(member)[index] || 0
        },
        async savePackageShare(member, night, val, isMain) {
            const value = val === '' || val === null ? 0 : Math.max(parseInt(val) || 0, 0)
            let payload
            if (night === null) {
                payload = { package_groupsize: value }
            } else {
                // Send every night of this package's stay, as shown, with the edited one changed.
                const nights = Math.max(this.getAccomNightCount(member), 1)
                const start = this.$date(member.start_date).startOf('day')
                const shares = Array.from({ length: nights }, (unused, i) => this.getDisplayShare(member, start.add(i, 'day'), isMain))
                shares[this.$date(night).diff(start, 'day')] = value
                payload = { package_accom_groupsize: shares }
            }
            try {
                const { data } = await this.$axios.post(`admin/quotes/update_package_groupsize/${member.id}`, payload)
                if (!data.response) {
                    this.$toast.error(data.message || 'Failed to update package groupsize')
                    return
                }
                // Update the loaded event in place: the edited share, any shares the API seeded,
                // and the recalculated supplier total.
                const storeMember = this.findSourceBuild(member.id)
                if (storeMember) Object.assign(storeMember, payload)
                ;(data.members || []).forEach(updated => {
                    const storeBuild = this.findSourceBuild(updated.id)
                    if (storeBuild) Object.assign(storeBuild, updated)
                })
                // Keep the shown build's history current: the groupsize email's Old vs New reads it.
                if (data.data && Array.isArray(data.groupsize_history)) {
                    const storeShown = this.findSourceBuild(data.data.id)
                    if (storeShown) storeShown.groupsize_history = data.groupsize_history
                }
                this.$toast.success('Groupsize updated')
            } catch (error) {
                this.$toast.error(error.response?.data?.message || 'Failed to update package groupsize')
            }
        },
        groupQuoteNames(grouped_builds) {
            const names = []
            grouped_builds.forEach(build => {
                [build, ...(build.linked_builds || [])].forEach(member => {
                    if (member.quote && !names.includes(member.quote.name)) names.push(member.quote.name)
                })
            })
            return names
        },
        hasAttendanceOnDay(build, dayName) {
            if (build.type_id == 1) {
                // Accommodation - check nights (exclude checkout day)
                const nights = this.$date(build.end_date).diff(this.$date(build.start_date), 'day')
                for (let i = 0; i < nights; i++) {
                    if (this.$date(build.start_date).add(i, 'day').format('dddd') === dayName) {
                        return true
                    }
                }
                return false
            } else {
                // Activity - check if start date matches
                return this.$date(build.start_date).format('dddd') === dayName
            }
        },
        groupBy(array, value) {
            return _.groupBy(array, value)
        },
        getStatusClass(id) {
            const map = { 1: 'status-pill--1', 2: 'status-pill--2', 3: 'status-pill--3', 4: 'status-pill--4', 5: 'status-pill--5' }
            return map[id] || 'status-pill--1'
        },
        getPaymentStatusClass(id) {
            const map = { 1: 'payment-pill--1', 2: 'payment-pill--2', 3: 'payment-pill--3', 4: 'payment-pill--4' }
            return map[id] || 'payment-pill--1'
        },
        isStatusLocked(id) {
            if (this.get_user.id === 1) return false
            return id === 3 || id === 4
        },
        openEditSupplierEmailDialog(supplier) {
            this.editing_supplier = supplier
            this.edit_supplier_email = supplier.email || ''
            this.edit_supplier_email_dialog = true
        },
        closeEditSupplierEmailDialog() {
            this.edit_supplier_email_dialog = false
            this.editing_supplier = null
            this.edit_supplier_email = ''
        },
        async confirmEditSupplierEmail() {
            const supplier = this.editing_supplier
            const newEmail = (this.edit_supplier_email || '').trim()
            if (!supplier) return
            try {
                const { data } = await this.$axios.patch('api/suppliers/change_email', {
                    supplier_id: supplier.id,
                    company_id: this.get_selected_company.id,
                    email: newEmail,
                })
                if (data.response) {
                    supplier.email = newEmail
                    this.$toast.success('Supplier email updated')
                    this.closeEditSupplierEmailDialog()
                } else {
                    this.$toast.error('Failed to update supplier email')
                }
            } catch (e) {
                this.$toast.error('Failed to update supplier email')
            }
        },
        toggleDeleteBuild(buildId) {
            const newSet = new Set(this.selected_build_ids)
            if (newSet.has(buildId)) {
                newSet.delete(buildId)
            } else {
                newSet.add(buildId)
            }
            this.selected_build_ids = newSet
        },
        openUpdateGroupsizeDialog() {
            this.update_groupsize_dialog = true
        },
        closeUpdateGroupsizeDialog() {
            this.update_groupsize_dialog = false
        },
        openSendEmailDialog(item) {
            this.send_email_dialog = true
            this.supplier_to_send_to = item
        },
        closeSendEmailDialog() {
            this.send_email_dialog = false
            this.supplier_to_send_to = null
        },
        updateQuoteBuildAccomAndSendDialog(item, tmp_groupsize) {
            this.send_email_dialog_accom = true
            this.supplier_to_send_to = item
            this.groupsize_to_send = tmp_groupsize
        },
        closeSendEmailAccomDialog() {
            this.send_email_dialog_accom = false
            this.supplier_to_send_to = null
            this.groupsize_to_send = null
        },
        openFinalGroupsizeDialog() {
            this.final_groupsize_dialog = true
        },
        closeFinalGroupsizeDialog() {
            this.final_groupsize_dialog = false
        },
        // Every supplier on the tab, live or deleted services.
        async loadSentServices() {
            const supplierIds = [...this.combined_builds, ...this.deleted_builds].map(build => build.supplier_id)
            this.sent_services = await loadSentServices(this.get_selected_company.id, this.event.id, supplierIds)
        },
        onManualEmailSent() {
            this.ticked_build_ids = []
            this.loadSentServices()
            this.$emit('refresh')
        },
        // The tick boxes on the page: services to start ticked in Email Supplier/s and Delete.
        // Any suppliers' services; nothing ticked still allows emailing everyone.
        tickedIn(builds) {
            return builds.filter(build => this.ticked_build_ids.includes(build.id)).map(build => build.id)
        },
        toggleTicked(buildId) {
            this.ticked_build_ids = this.ticked_build_ids.includes(buildId)
                ? this.ticked_build_ids.filter(id => id !== buildId)
                : [...this.ticked_build_ids, buildId]
        },
        toggleTickedAll(grouped_builds) {
            const ids = grouped_builds.map(build => build.id)
            const all = ids.every(id => this.ticked_build_ids.includes(id))
            this.ticked_build_ids = all
                ? this.ticked_build_ids.filter(id => !ids.includes(id))
                : [...new Set([...this.ticked_build_ids, ...ids])]
        },
        // Ticked services among these builds (one supplier's, or the whole tab), or null when none.
        tickedOrNull(builds) {
            const ids = this.tickedIn(builds)
            return ids.length ? ids : null
        },
        async loadTemplates() {
            this.loading_templates = true
            try {
                this.email_templates = await loadEmailTemplates(this.get_selected_company.id)
            } catch (error) {
                this.email_templates = []
                this.$toast.error('Failed to load email templates')
            }
            this.loading_templates = false
        },
        // Email Supplier/s: a template (null: a blank email) to one supplier, or to every
        // supplier from the top bar. All their services start ticked, or only the ones ticked on
        // the page (and, from the top bar, only their suppliers); untick any in the dialog.
        // Request Availability from the Email Supplier/s menu: this supplier (or every supplier, or
        // only the ticked services) with its booked products picked, in the panel at the bottom.
        openAvailability(grouped_builds = null) {
            const builds = grouped_builds || this.combined_builds
            const ticked = this.tickedOrNull(builds)
            const preset = availabilityPreset(ticked ? builds.filter(build => ticked.includes(build.id)) : builds)
            this.$refs.availability?.requestFor(preset.supplier_ids, preset.product_ids)
        },
        openTemplateEmails(template, grouped_builds = null) {
            const ticked = this.tickedOrNull(grouped_builds || this.combined_builds)
            const items = templateEmailItems(grouped_builds ? [grouped_builds] : this.builds, template, {
                event: this.event,
                currency: this.get_selected_company.currency,
                selected: ticked && !grouped_builds ? builds => builds.some(build => ticked.includes(build.id)) : null,
                serviceSelected: ticked ? build => ticked.includes(build.id) : null,
            })
            if (!items.length) {
                this.$toast.error('No suppliers to email')
                return
            }
            this.openManualEmail({
                title: grouped_builds ? `Email Supplier - ${items[0].supplier.supplier_name}` : 'Email Suppliers',
                subtitle: `${template ? template.subject : 'Blank email'}. Untick any supplier or service you don't want to email, then send.`,
                items,
                note: templateEmailNote(template),
            })
        },
        // Final groupsizes to every supplier with Confirmed bookings: the company's email template
        // with "Groupsize" in its subject, filled in per supplier (its Confirmed services ticked),
        // previewed before sending.
        async openFinalGroupsizeEmails() {
            if (!this.email_templates || !this.email_templates.length) {
                this.loading_groupsize_emails = true
                await this.loadTemplates()
                this.loading_groupsize_emails = false
            }
            const groupsizeTemplate = (this.email_templates || []).find(template =>
                template.subject && template.subject.toLowerCase().includes('groupsize')
            )
            if (!groupsizeTemplate) {
                this.$toast.error('No template with "Groupsize" in subject found')
                return
            }

            const isConfirmed = build => build.booking_status_id === 3
            const items = templateEmailItems(this.builds.filter(builds => builds.some(isConfirmed)), groupsizeTemplate, {
                event: this.event,
                currency: this.get_selected_company.currency,
                selected: () => true,
                serviceSelected: isConfirmed,
            })
            if (!items.length) {
                this.$toast.error('No confirmed bookings to send')
                return
            }
            this.openManualEmail({
                title: 'Send Final Groupsizes to Suppliers',
                subtitle: 'Suppliers with Confirmed bookings, their Confirmed services ticked. Check each email, untick anything you don\'t want to send, then send.',
                email_type: 'groupsize',
                items,
            })
        },
        // A supplier's deleted services, once each (a copy on a variant package is the same booking).
        deletedBuildsFor(supplierId) {
            const [builds = []] = deletedBuildGroups(this.deleted_builds, supplierId)
            return builds
        },
        // Email Supplier/s → Booking changes, for every supplier or one supplier's panel: a dates
        // changed, booking changed or cancellation email built from the services as they are now
        // (or the deleted ones, for a cancellation). The prompt after an edit can also show what
        // they were.
        openChangeEmails(kind, grouped_builds = null) {
            const supplierId = grouped_builds ? grouped_builds[0].supplier_id : null
            const groups = kind === 'cancellation'
                ? deletedBuildGroups(this.deleted_builds, supplierId)
                : (grouped_builds ? [grouped_builds] : this.builds)
            const dialog = changeEmailDialog(kind, groups, {
                event: this.event,
                company: this.get_selected_company,
                advisorName: this.get_user ? this.get_user.name : '',
                currency: this.get_selected_company.currency,
                single: !!grouped_builds,
                // The page's ticks pick live services, not deleted ones.
                ticked: kind === 'cancellation' ? null : this.tickedOrNull(grouped_builds || this.combined_builds),
            })
            if (!dialog.items.length) {
                this.$toast.error('No services to email about')
                return
            }
            this.openManualEmail(dialog)
        },
        openManualEmail({ title, subtitle = '', email_type = null, items, note = '' }) {
            this.manual_email = { show: true, title, subtitle, email_type, items, note }
        },
        closeManualEmail() {
            this.manual_email = { ...this.manual_email, show: false }
        },
        async updateFinalGroupsizes() {
            const buildsToUpdate = []

            // Loop through all builds. A shown build is the supplier's booking, so it gets the
            // guests of the variant quotes folded into it too; the hidden copies keep their own
            // quote's count for that quote's itinerary.
            const shown = new Set(this.combined_builds)
            this.all_builds.forEach(build => {
                const isShown = shown.has(build)
                const paidCount = isShown ? this.getBookingPaidCount(build) : this.getPaidInFullCount(build)
                if (build.type_id == 1) {
                    // Accommodation - update accom_groupsize array
                    const nights = this.getAccomNightCount(build)
                    const groupsizeArray = Array.from({ length: nights }, (_, i) => isShown
                        ? this.getBookingPaidCount(build, this.getAccomNightDate(build, i))
                        : paidCount)
                    const groupsizeString = JSON.stringify(groupsizeArray)

                    const update = {
                        id: build.id,
                        accom_groupsize: groupsizeString
                    }
                    // Shared with other packages: also fill this package's own numbers (Package rows)
                    if (!isShown || this.hasPackageSplit(build)) {
                        update.package_accom_groupsize = Array(nights).fill(this.getPaidInFullCount(build))
                    }
                    buildsToUpdate.push(update)
                    // Update local data immediately
                    build.accom_groupsize = groupsizeString
                } else {
                    // Activity - update groupsize
                    const update = {
                        id: build.id,
                        groupsize: paidCount
                    }
                    if (!isShown || this.hasPackageSplit(build)) {
                        update.package_groupsize = this.getPaidInFullCount(build)
                    }
                    buildsToUpdate.push(update)
                    // Update local data immediately
                    build.groupsize = paidCount
                }
            })

            // Update all builds in backend. Hidden variant copies first: the shown build's groupsize
            // history records every package's numbers, so they need to be saved before it is.
            try {
                const save = builds => Promise.all(builds.map(buildData =>
                    this.$axios.post('admin/quotes/update_quote_build_groupsize', buildData)
                ))
                const shownIds = new Set(this.combined_builds.map(build => build.id))
                await save(buildsToUpdate.filter(buildData => !shownIds.has(buildData.id)))
                await save(buildsToUpdate.filter(buildData => shownIds.has(buildData.id)))
                this.$toast.success('All groupsizes updated successfully')
                this.$emit('refresh')
            } catch (error) {
                this.$toast.error('Failed to update groupsizes')
                console.error('Error updating groupsizes:', error)
            }

            this.closeFinalGroupsizeDialog()
        },
        findSourceBuild(buildId) {
            for (const quote of this.event.quotes) {
                const found = quote.builds.find(b => b.id === buildId)
                if (found) return found
            }
            return null
        },
        // The API mirrors booking fields onto a build's hidden copies on variant quotes; do the
        // same in the loaded event so they agree until the next refresh.
        setOnStoreBuilds(build, field) {
            [build, ...(build.linked_builds || [])].forEach(member => {
                const storeBuild = this.findSourceBuild(member.id)
                if (storeBuild) storeBuild[field] = build[field]
            })
        },
        async updateBookingStatus(build) {
            let payload = {
                booking_status_id: build.booking_status_id,
            }
            await this.$axios.post(`admin/events/update_booking_status/${build.id}`, payload)
                .then(({ data }) => {
                    if (data.response) {
                        this.setOnStoreBuilds(build, 'booking_status_id')
                        this.$toast.success(data.message)
                    }
                })
        },
        async updatePaymentStatus(build) {
            let payload = {
                payment_status_id: build.payment_status_id,
            }
            await this.$axios.post(`admin/events/update_payment_status/${build.id}`, payload)
                .then(({ data }) => {
                    if (data.response) {
                        this.setOnStoreBuilds(build, 'payment_status_id')
                        this.$toast.success(data.message)
                    }
                })
        },
        parsedAccomGroupsize(build) {
            if (!build.accom_groupsize) return []
            if (Array.isArray(build.accom_groupsize)) return build.accom_groupsize
            if (typeof build.accom_groupsize === 'string') {
                try {
                    const parsed = JSON.parse(build.accom_groupsize)
                    return Array.isArray(parsed) ? parsed : []
                } catch (e) {
                    console.warn('Invalid accom_groupsize JSON for build', build.id, '- returning empty array')
                    return []
                }
            }
            return []
        },
        parsedAccomFinalRates(build) {
            if (!build.accom_final_rates) return []
            if (Array.isArray(build.accom_final_rates)) return build.accom_final_rates
            if (typeof build.accom_final_rates === 'string') {
                try {
                    const parsed = JSON.parse(build.accom_final_rates)
                    return Array.isArray(parsed) ? parsed : []
                } catch (e) {
                    console.warn('Invalid accom_final_rates JSON for build', build.id, '- returning empty array')
                    return []
                }
            }
            return []
        },
        getAccomNightCount(build) {
            if (build.start_date && build.end_date) {
                const nights = this.$date(build.end_date).diff(this.$date(build.start_date), 'day')
                if (nights > 0) return nights
            }
            return this.parsedAccomGroupsize(build).length || 1
        },
        getAccomNightIndices(build) {
            return Array.from({ length: this.getAccomNightCount(build) }, (_, i) => i)
        },
        optionTypeNames(build) {
            const typeId = build && build.type_id
            if (!typeId) return ''
            return this.get_supplier_type.find((t) => t.id === typeId)?.name || ''
        },
        getNightLabel(build, nightIdx) {
            return this.$date(build.start_date).add(nightIdx, 'day').format('ddd DD/MM/YYYY')
        },
        updateAccomNight(build, index, val) {
            const nights = this.getAccomNightCount(build)
            let arr = this.parsedAccomGroupsize(build)
            while (arr.length < nights) arr.push(0)
            arr[index] = parseInt(val) || 0
            build.accom_groupsize = JSON.stringify(arr)
            this.saveBuildAccomGroupsize(build)
        },
        updateAccomFinalRate(build, index, val) {
            const nights = this.getAccomNightCount(build)
            let arr = this.parsedAccomFinalRates(build)
            while (arr.length < nights) arr.push('0')
            arr[index] = String(parseFloat(val) || 0)
            build.accom_final_rates = JSON.stringify(arr)
            this.saveBuildAccomFinalRates(build)
        },
        async saveBuildStartTime(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build', build)).data
            if (result.response) {
                this.$toast.success('Start time updated')
            }
        },
        async saveBuildGroupsize(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', build)).data
            if (result.response) {
                this.$toast.success('Groupsize updated')
                this.$emit('refresh')
            }
        },
        async saveBuildAccomGroupsize(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', build)).data
            if (result.response) {
                this.$toast.success('Groupsize updated')
                this.$emit('refresh')
            }
        },
        async saveBuildFinalRate(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_final_rates', build)).data
            if (result.response) {
                this.$toast.success('Final rate updated')
            }
        },
        async saveBuildAccomFinalRates(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_final_rates', build)).data
            if (result.response) {
                this.$toast.success('Final rate updated')
            }
        },
        async saveBuildBookingNotes(build) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build', build)).data
            if (result.response) {
                this.$toast.success('Booking notes updated')
            }
        },
        async initializeFinalRates() {
            const buildsToUpdate = []

            if (this.builds) {
                Object.values(this.builds).forEach(groupedBuilds => {
                    groupedBuilds.forEach(build => {
                        if (build.type_id == 1) {
                            // Accommodation - initialize accom_final_rates only if not already set
                            if (!build.accom_final_rates) {
                                const nights = this.getAccomNightCount(build)
                                const rates = Array(nights).fill(String(defaultFinalRate(build)))
                                const ratesString = JSON.stringify(rates)
                                buildsToUpdate.push({
                                    id: build.id,
                                    accom_final_rates: ratesString
                                })
                                // Update local data immediately
                                build.accom_final_rates = ratesString
                            }
                        } else {
                            // Activity - initialize final_rate only if not already set
                            if (build.final_rate === null || build.final_rate === undefined) {
                                const initialRate = defaultFinalRate(build)
                                buildsToUpdate.push({
                                    id: build.id,
                                    final_rate: initialRate
                                })
                                // Update local data immediately
                                build.final_rate = initialRate
                            }
                        }
                    })
                })
            }

            // Silently save all builds that need initialization in bulk
            if (buildsToUpdate.length > 0) {
                await this.$axios.post('admin/quotes/bulk_update_quote_build_final_rates', buildsToUpdate)
            }
        },
        async updateGroupsizes() {
            // this.event.quotes.forEach(quote => {
            //     let attendees = this.event.invitees.filter((x) => x.payments.filter((y) => y.payment_details.filter((z) => z.payment_type_id == 1).length > 0).length > 0).length
            //     quote.builds.forEach(build => {
            //         if (build.type_id == 1) {
            //             let accom_groupsize = JSON.parse(build.accom_groupsize)
            //             for (let i = 0; i < accom_groupsize.length; i++) {
            //                 accom_groupsize[i] = attendees
            //             }
            //             build.accom_groupsize = JSON.stringify(accom_groupsize)
            //         }
            //         else {
            //             build.groupsize = attendees
            //         }
            //     })
            // })

            this.event.quotes.forEach(quote => {
                let attendees = this.event.invitees.filter(invitee =>
                    detailsAssignedTo(this.event, invitee.id).some(detail => detail.payment_type_id == 1)
                ).length

                quote.builds.forEach(build => {
                    if (build.type_id === 1) {
                        let accom_groupsize = []

                        if (build.accom_groupsize) {
                            // Check if it's already an array
                            if (Array.isArray(build.accom_groupsize)) {
                                accom_groupsize = build.accom_groupsize
                            } else if (typeof build.accom_groupsize === 'string') {
                                try {
                                    let parsed = JSON.parse(build.accom_groupsize)
                                    if (Array.isArray(parsed)) {
                                        accom_groupsize = parsed
                                    }
                                } catch (error) {
                                    console.warn("Failed to parse accom_groupsize for build", build.id, "- resetting to empty array")
                                    // Initialize with the number of nights
                                    const nights = this.$date(build.end_date).diff(this.$date(build.start_date), 'day')
                                    accom_groupsize = Array(nights > 0 ? nights : 1).fill(0)
                                }
                            }
                        }

                        for (let i = 0; i < accom_groupsize.length; i++) {
                            accom_groupsize[i] = attendees
                        }

                        build.accom_groupsize = JSON.stringify(accom_groupsize)
                    } else {
                        build.groupsize = attendees
                    }
                })
            })

            const result = await (await this.$axios.post('admin/quotes/update_groupsizes', this.builds)).data
            await this.$axios.patch(`admin/quotes/is_fully_paid/${this.event.id}`)
            if (result.response == true) {
                this.closeUpdateGroupsizeDialog()
                this.$toast.success(result.message)
                this.$emit('refresh')
            }
        },
        async updateQuoteBuildAccom(item, groupsize) {
            item.accom_groupsize = JSON.stringify(groupsize)
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async updateQuoteBuildAccomAndSend(item, groupsize) {
            item.accom_groupsize = JSON.stringify(groupsize)
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize_and_send', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async addAccomGroupsize(item) {
            let accom_groupsize = JSON.parse(item.accom_groupsize)
            if (accom_groupsize.length > 2) {
                this.$toast.error('You cannot add any more days')
                return
            }
            accom_groupsize.push(accom_groupsize[0])
            item.accom_groupsize = JSON.stringify(accom_groupsize)
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async removeAccomGroupsize(item) {
            let accom_groupsize = JSON.parse(item.accom_groupsize)
            if (accom_groupsize.length < 2) {
                this.$toast.error('You cannot remove any more days')
                return
            }
            accom_groupsize = accom_groupsize.slice(0, -1)
            item.accom_groupsize = JSON.stringify(accom_groupsize)
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async updateQuoteBuild(item) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async updateQuoteBuildAndSend(item) {
            const result = await (await this.$axios.post('admin/quotes/update_quote_build_groupsize_and_send', item)).data
            if (result.response == true) {
                this.$toast.success(result.message)
            }
        },
        async openHistoryDialog(item) {
            this.history_dialog = true
            this.history_loading = true
            this.booking_history = []
            this.selected_supplier = item[0].supplier
            let payload = {
                company_id: this.get_selected_company.id,
                event_id: this.event.id,
                supplier_id: this.selected_supplier.id,
            }
            const result = await (await this.$axios.get('admin/events/get_booking_history', payload)).data
            if (result.response == true) {
                this.booking_history = result.data.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            }
            this.history_loading = false
        },
        closeHistoryDialog() {
            this.history_dialog = false
        },
        openGroupsizeHistoryDialog(item) {
            this.groupsize_history_dialog = true
            this.selected_supplier = item[0].supplier

            // Collect all groupsize history records grouped by build
            const groupedByBuild = {}

            item.forEach(build => {
                if (build.groupsize_history && build.groupsize_history.length > 0) {
                    const serviceName = build.option ? build.option.name : 'Unknown Service'
                    const buildKey = `${build.id}_${serviceName}`

                    if (!groupedByBuild[buildKey]) {
                        groupedByBuild[buildKey] = {
                            service_name: serviceName,
                            is_accommodation: build.type_id == 1,
                            history: []
                        }
                    }

                    build.groupsize_history.forEach(history => {
                        let accom_groupsize = []
                        if (history.accom_groupsize) {
                            if (Array.isArray(history.accom_groupsize)) {
                                accom_groupsize = history.accom_groupsize
                            } else if (typeof history.accom_groupsize === 'string') {
                                try {
                                    const parsed = JSON.parse(history.accom_groupsize)
                                    accom_groupsize = Array.isArray(parsed) ? parsed : []
                                } catch (e) {
                                    console.warn('Failed to parse accom_groupsize:', e)
                                }
                            }
                        }

                        groupedByBuild[buildKey].history.push({
                            id: history.id,
                            is_accommodation: build.type_id == 1,
                            groupsize: history.groupsize,
                            accom_groupsize: accom_groupsize,
                            packages: Array.isArray(history.packages) ? history.packages : null,
                            created_at: history.created_at
                        })
                    })
                }
            })

            // Convert to array and sort history within each build
            this.groupsize_history_list = Object.values(groupedByBuild).map(group => {
                // Sort history by created_at descending (most recent first) within each group
                group.history.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                // Mark what changed since the entry before (the next one down the list)
                group.history = group.history.map((record, index) => this.describeGroupsizeChange(record, group.history[index + 1] || null))
                return group
            })

            // Sort groups by most recent history entry
            this.groupsize_history_list.sort((a, b) => {
                const aLatest = a.history.length > 0 ? new Date(a.history[0].created_at) : new Date(0)
                const bLatest = b.history.length > 0 ? new Date(b.history[0].created_at) : new Date(0)
                return bLatest - aLatest
            })
        },
        // One groupsize history entry for display, compared with the entry before it: the supplier
        // total per day, and (for a build shared by several packages) each package's own numbers,
        // with the previous value on anything that changed.
        describeGroupsizeChange(record, previous) {
            const change = (value, before, hasBefore) => {
                const was = hasBefore && before !== value ? before : undefined
                return {
                    value,
                    was,
                    direction: was === undefined || was === null || value === null ? null : (value > was ? 'up' : 'down'),
                }
            }
            const parseShares = shares => {
                if (Array.isArray(shares)) return shares
                if (typeof shares === 'string') {
                    try { return JSON.parse(shares) || [] } catch (e) { return [] }
                }
                return []
            }

            const total = record.is_accommodation
                ? record.accom_groupsize.map((size, i) => ({
                    label: `Day ${i + 1}`,
                    ...change(size, previous ? previous.accom_groupsize[i] : undefined, !!previous),
                }))
                : [{ label: 'Groupsize', ...change(record.groupsize, previous ? previous.groupsize : undefined, !!previous) }]

            let packages = null
            if (record.packages) {
                const days = record.is_accommodation ? record.accom_groupsize.length : 1
                const mainStart = this.$date((record.packages.find(p => p.is_main) || record.packages[0]).start_date)
                packages = record.packages.map(pkg => {
                    const before = previous && previous.packages
                        ? previous.packages.find(p => p.build_id === pkg.build_id) || null
                        : null
                    let rows
                    if (record.is_accommodation) {
                        // Line each package's nights up with the supplier total's days.
                        const offset = this.$date(pkg.start_date).diff(mainStart, 'day')
                        const nights = Math.max(this.$date(pkg.end_date).diff(this.$date(pkg.start_date), 'day'), 1)
                        const shares = parseShares(pkg.accom_groupsize)
                        const beforeShares = before ? parseShares(before.accom_groupsize) : []
                        rows = Array.from({ length: days }, (unused, i) => {
                            const index = i - offset
                            if (index < 0 || index >= nights) return { label: `Day ${i + 1}`, not_staying: true }
                            return {
                                label: `Day ${i + 1}`,
                                ...change(shares[index] ?? null, beforeShares[index] ?? null, !!before),
                            }
                        })
                    } else {
                        rows = [{ label: 'Groupsize', ...change(pkg.groupsize ?? null, before ? before.groupsize ?? null : null, !!before) }]
                    }
                    return {
                        name: pkg.quote_name || 'Package',
                        is_main: pkg.is_main,
                        rows,
                        changed: rows.some(row => row.was !== undefined),
                    }
                })
            }

            return {
                ...record,
                total,
                packages,
                changed_packages: packages ? packages.filter(pkg => pkg.changed).map(pkg => pkg.name) : [],
                total_changed: total.some(row => row.was !== undefined),
                is_first: !previous,
            }
        },
        closeGroupsizeHistoryDialog() {
            this.groupsize_history_dialog = false
            this.groupsize_history_list = []
        },
        // A supplier's delete button: tick which of its services to delete in the dialog.
        openDeleteConfirmationDialog(grouped_builds) {
            this.delete_builds = grouped_builds
            // Starts with this supplier's services ticked on the page.
            this.selected_build_ids = new Set(this.tickedIn(grouped_builds))
            this.delete_linked_choice = null
            this.delete_confirmation_dialog = true
        },
        closeDeleteConfirmationDialog() {
            this.delete_confirmation_dialog = false
            this.delete_linked_choice = null
            this.delete_builds = []
            this.selected_build_ids = new Set()
        },
        async confirmDeleteBuilds() {
            try {
                const buildIds = Array.from(this.selected_build_ids)
                if (this.delete_linked_choice === 'all') {
                    buildIds.push(...this.selectedLinkedBuilds.map(build => build.id))
                }
                const { data } = await this.$axios.post('admin/events/bulk_delete_quote_builds', {
                    build_ids: buildIds
                })
                this.$toast.success('Services deleted successfully', { timeout: 3000 })
                this.ticked_build_ids = this.ticked_build_ids.filter(id => !buildIds.includes(id))
                this.closeDeleteConfirmationDialog()
                // Booked services deleted: offer the supplier a cancellation email.
                this.supplier_changes = data.supplier_changes || null
                await this.loadDeletedBuilds()
                this.$emit('refresh')
            } catch (error) {
                this.$toast.error('Failed to delete services')
                console.error('Delete error:', error)
            }
        },
        async loadDeletedBuilds() {
            try {
                this.loading_deleted_builds = true
                const response = await this.$axios.get(`admin/events/get_deleted_quote_builds/${this.event.id}`)
                if (response.data.response === true) {
                    this.deleted_builds = response.data.data
                    this.loading_deleted_builds = false
                }
            } catch (error) {
                console.error('Error loading deleted builds:', error)
            }
        },
        openViewDeletedDialog() {
            this.view_deleted_dialog = true
            // Open all expansion panels by default
            this.$nextTick(() => {
                const supplierCount = Object.keys(this.getDeletedBuildsBySupplier()).length
                this.deleted_panels_open = Array.from({ length: supplierCount }, (_, i) => i)
            })
        },
        closeViewDeletedDialog() {
            this.view_deleted_dialog = false
            this.selected_deleted_build_ids = new Set()
            this.deleted_panels_open = []
        },
        toggleDeletedBuildSelection(buildId) {
            const newSet = new Set(this.selected_deleted_build_ids)
            if (newSet.has(buildId)) {
                newSet.delete(buildId)
            } else {
                newSet.add(buildId)
            }
            this.selected_deleted_build_ids = newSet
        },
        toggleSelectAllDeletedBuilds(grouped_builds) {
            const newSet = new Set(this.selected_deleted_build_ids)
            const allSelected = grouped_builds.every(build => newSet.has(build.id))
            grouped_builds.forEach(build => {
                if (allSelected) {
                    newSet.delete(build.id)
                } else {
                    newSet.add(build.id)
                }
            })
            this.selected_deleted_build_ids = newSet
        },
        async restoreSelectedBuilds() {
            try {
                const buildIds = Array.from(this.selected_deleted_build_ids)
                await this.$axios.post('admin/events/bulk_restore_quote_builds', {
                    build_ids: buildIds
                })
                this.$toast.success('Services restored successfully', { timeout: 3000 })
                this.closeViewDeletedDialog()
                await this.loadDeletedBuilds()
                this.$emit('refresh')
            } catch (error) {
                this.$toast.error('Failed to restore services')
                console.error('Restore error:', error)
            }
        },
        getDeletedBuildsBySupplier() {
            // Order the same way as combined_builds/builds so deleted services line up with the live views
            const ordered_deleted_builds = _.orderBy(this.deleted_builds, [accommodationFirst, 'start_date', (build) => BUILD_TYPE_ORDER.indexOf(build.type_id)], ['asc', 'asc', 'asc'])
            const grouped = _.groupBy(ordered_deleted_builds, 'supplier_id')
            const orderedSupplierIds = []
            ordered_deleted_builds.forEach(build => {
                if (!orderedSupplierIds.includes(build.supplier_id)) {
                    orderedSupplierIds.push(build.supplier_id)
                }
            })
            return orderedSupplierIds
                .map(supplierId => grouped[supplierId])
                .filter(builds => builds && builds.length > 0)
        }
    }
}
</script>

<style lang="scss" scoped>
.card-format {
    border-radius: 15px !important;

    .v-data-footer {
        background-color: #E9E9E9;
        color: #7C7C7C;
        font-family: 'Work Sans';
    }

    .v-data-footer__select .v-text-field {
        background-color: white;

        .v-select__slot {
            padding-left: 10px;
        }
    }
}

.custom-small {
    color: #7C7C7C;
    font-size: 12px;
    font-weight: 600;
}

:deep(.theme--light.v-data-table tbody td) {
    height: 40px !important;
    border-right: thin solid rgba(0, 0, 0, 0.12) !important;
}

.label-text-color {
    color: #7C7C7C;
}

.text-proper-case {
    text-transform: capitalize;
}

.search-text-field {
    margin-top: 15px;

    .v-input__slot {
        min-height: 0 !important;
        margin-top: 8px;
    }

    .v-input__prepend-inner {
        margin-top: 5px !important;
    }
}

:deep(.custom-buttons span) {
    text-transform: none;
    font-family: Manrope;
    font-size: 13px !important;
    font-style: normal;
    font-weight: 600 !important;
    line-height: normal;
    box-shadow: none !important;
}

:deep(.custom-buttons button) {
    padding: 8px 7px 8px 7px !important;
    border-radius: 5px;
}

.paid_row {
    background-color: #FFF1F1 !important;
}


:deep(.custom-text-field) {
    legend {
        display: none;
    }

    fieldset {
        border-color: #D3D3D3 !important;
    }

    input {
        color: #101010;
        font-family: Manrope;
        font-size: 12px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
    }
}

::-webkit-scrollbar {
    width: 7px;
    background: white;
}

::-webkit-scrollbar-thumb {
    border-radius: 2px;
    background-color: #D9D9D9;
    color: #D9D9D9;
    height: 50px;
}

.label-1 {
    color: #7C7C7C;
    background-color: #F7F7F7;
    font-size: 20px;
    border: solid 1px #7C7C7C;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-2 {
    color: #EE9E25;
    background-color: #FFF3E2;
    font-size: 20px;
    border: solid 1px #EE9E25;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-3 {
    color: #669E53;
    background-color: #E6F3E2;
    font-size: 20px;
    border: solid 1px #669E53;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-4 {
    color: #AA3939;
    background-color: #FFCFCF;
    font-size: 20px;
    border: solid 1px #AA3939;
    border-radius: 4px;
    padding: 3px 5px;
}

.label-5 {
    color: #6A4C93;
    background-color: #EFE6F8;
    font-size: 20px;
    border: solid 1px #9B7BC4;
    border-radius: 4px;
    padding: 3px 5px;
}

.custom-expansion-panels {
    padding-right: 9px;
    padding-bottom: 50px;
}

.custom-expansion-panel::before {
    box-shadow: none !important;
}

.custom-expansion-panel::after {
    border-top: none !important;
}

.v-expansion-panel--active,
.v-expansion-panel--active+.v-expansion-panel {
    margin-top: 0px !important;
}

.v-expansion-panel-header--active {
    border-radius: 10px 10px 0px 0px !important;
    min-height: 0px !important;
}

:deep(.custom-expansion-header) {
    border-radius: 10px;
    background: var(--Blue-Linear, #3988AA);
    color: var(--icon-color, #FFF);
    font-size: 14px !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;

    .v-icon {
        color: white !important;
    }
}

.custom-expansion-panel-content {
    border-radius: 0px 0px 10px 10px;
    border: 1px solid var(--Border, #E9E9E9);
    background: var(--icon-color, #FFF);
    box-shadow: 5px 5px 13px 0px rgba(240, 240, 240, 0.90), -5px -5px 10px 0px rgba(255, 255, 255, 0.90), 5px -5px 10px 0px rgba(240, 240, 240, 0.20), -5px 5px 10px 0px rgba(240, 240, 240, 0.20);
}

.booking-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    max-height: 90vh;
}

.booking-dialog-card>.v-card__text {
    overflow-y: auto;
    flex: 1;
}

.booking-dialog-header {
    background: linear-gradient(135deg, #3988AA 0%, #2a6a88 100%);
}

.booking-dialog-title {
    font-family: Manrope;
    font-size: 18px;
    font-weight: 700;
    color: white;
    display: flex;
    align-items: center;
}

.booking-dialog-subtitle {
    font-family: Manrope;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.80);
    font-weight: 500;
    padding-left: 28px;
}

.booking-section-header {
    display: flex;
    align-items: center;
    color: #7C7C7C;
    font-family: Manrope;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.history-dialog-card {
    border-radius: 12px !important;
    overflow: hidden;
}

.history-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #AAAAAA;
    font-family: Manrope;
    font-size: 14px;
    font-weight: 500;
}

.history-list {
    max-height: 60vh;
    overflow-y: auto;
}

.history-item {
    border-radius: 8px;
    border: 1px solid #E9E9E9;
    overflow: hidden;
}

.history-item-header {
    background-color: #F7F9FB;
    border-bottom: 1px solid #E9E9E9;
}

.history-subject {
    font-family: Manrope;
    font-size: 13px;
    font-weight: 700;
    color: #101010;
}

.history-meta {
    font-family: Manrope;
    font-size: 12px;
    color: #7C7C7C;
    font-weight: 500;
    white-space: nowrap;
}

.history-item-body {
    background-color: #FFFFFF;
    font-family: Manrope;
    font-size: 13px;
    color: #3D3D3D;
}

/* Groupsize history: supplier total, per package numbers, and what changed */
.history-section-title {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #2a6a88;
    margin-bottom: 4px;
}

.history-value-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 24px;
}

.history-value-label {
    min-width: 72px;
    font-weight: 600;
    color: #7C7C7C;
}

.history-value--changed {
    font-weight: 700;
    color: #101010;
}

.history-change {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    background-color: #F1F1F1;
    color: #7C7C7C;
}

.history-change--up {
    background-color: #E6F3E2;
    color: #3F7A2E;
}

.history-change--down {
    background-color: #FDECEC;
    color: #B23B3B;
}

.history-change .v-icon {
    color: inherit !important;
}

.history-packages {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 10px;
    margin-top: 12px;
}

.history-package {
    padding: 8px 12px;
    border: 1px solid #E3E8EC;
    border-radius: 6px;
    background-color: #F7FAFC;
}

.history-package--changed {
    border-color: #F0C36D;
    background-color: #FFF8EA;
}

.history-package-name {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
}

.package-not-staying-inline {
    font-size: 12px;
    font-style: italic;
    color: #B0B0B0;
}

.history-tag {
    display: inline-flex;
    align-items: center;
    padding: 1px 8px;
    border-radius: 10px;
    font-family: Manrope;
    font-size: 11px;
    font-weight: 600;
    background-color: #EEF5F8;
    color: #2a6a88;
    white-space: nowrap;
}

.history-tag--package {
    background-color: #FFF3DC;
    color: #B26A00;
}

.history-body-content {
    font-family: Manrope;
    font-size: 13px;
    color: #333333;
    line-height: 1.6;
    max-height: 220px;
    overflow-y: auto;
}

.supplier-panel-body {
    padding: 20px 8px 12px;
}

.supplier-panel-top-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
}

.supplier-status-col {
    display: flex;
    flex-direction: column;
}

.supplier-actions-col {
    display: flex;
    align-items: center;
    gap: 0;
}

.supplier-info-label {
    display: flex;
    align-items: center;
    font-family: Manrope;
    font-size: 11px;
    font-weight: 700;
    color: #7C7C7C;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.supplier-info-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.supplier-info-chip {
    display: flex;
    align-items: center;
    background-color: #F7F9FB;
    border: 1px solid #E9E9E9;
    border-radius: 6px;
    padding: 6px 12px;
}

.supplier-info-value {
    font-family: Manrope;
    font-size: 13px;
    color: #101010;
    font-weight: 500;
}

.status-select {
    border-radius: 20px !important;
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;

    :deep(fieldset) {
        border-radius: 20px !important;
        border-width: 1px !important;
    }

    :deep(.v-select__selection) {
        font-size: 12px;
        font-weight: 700;
    }

    :deep(input) {
        display: none;
    }
}

.status-select.status-pill--1 {
    :deep(fieldset) {
        border-color: #C0C0C0 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #7C7C7C !important;
    }

    :deep(.v-input__slot) {
        background-color: #F7F7F7 !important;
    }
}

.status-select.status-pill--2 {
    :deep(fieldset) {
        border-color: #EE9E25 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #B87000 !important;
    }

    :deep(.v-input__slot) {
        background-color: #FFF3E2 !important;
    }
}

.status-select.status-pill--3 {
    :deep(fieldset) {
        border-color: #669E53 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #4A7A3A !important;
    }

    :deep(.v-input__slot) {
        background-color: #E6F3E2 !important;
    }
}

.status-select.status-pill--4 {
    :deep(fieldset) {
        border-color: #CC5555 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #AA3939 !important;
    }

    :deep(.v-input__slot) {
        background-color: #FFCFCF !important;
    }
}

.status-select.status-pill--5 {
    :deep(fieldset) {
        border-color: #9B7BC4 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #6A4C93 !important;
    }

    :deep(.v-input__slot) {
        background-color: #EFE6F8 !important;
    }
}

.payment-status-select {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    border-radius: 20px !important;
    border-width: 1px !important;

    :deep(.v-select__selection) {
        font-size: 12px;
        font-weight: 700;
    }

    :deep(input) {
        display: none;
    }
}

.payment-status-select.payment-pill--1 {
    :deep(fieldset) {
        border-color: #C0C0C0 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #7C7C7C !important;
    }

    :deep(.v-input__slot) {
        background-color: #F7F7F7 !important;
    }
}

.payment-status-select.payment-pill--2 {
    :deep(fieldset) {
        border-color: #3988AA !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #1A5F7A !important;
    }

    :deep(.v-input__slot) {
        background-color: #E3F2F9 !important;
    }
}

.payment-status-select.payment-pill--3 {
    :deep(fieldset) {
        border-color: #669E53 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #4A7A3A !important;
    }

    :deep(.v-input__slot) {
        background-color: #E6F3E2 !important;
    }
}

.payment-status-select.payment-pill--4 {
    :deep(fieldset) {
        border-color: #CC5555 !important;
    }

    :deep(.v-select__selection),
    :deep(.v-icon) {
        color: #AA3939 !important;
    }

    :deep(.v-input__slot) {
        background-color: #FFCFCF !important;
    }
}

.status-pill {
    display: inline-flex;
    align-items: center;
    font-family: Manrope;
    font-size: 12px;
    font-weight: 700;
    border-radius: 20px;
    padding: 3px 12px;
    white-space: nowrap;
    border: 1px solid;
}

.status-pill--1 {
    color: #7C7C7C;
    background-color: #F7F7F7;
    border-color: #C0C0C0;
}

.status-pill--2 {
    color: #B87000;
    background-color: #FFF3E2;
    border-color: #EE9E25;
}

.status-pill--3 {
    color: #4A7A3A;
    background-color: #E6F3E2;
    border-color: #669E53;
}

.status-pill--4 {
    color: #AA3939;
    background-color: #FFCFCF;
    border-color: #CC5555;
}

.status-pill--5 {
    color: #6A4C93;
    background-color: #EFE6F8;
    border-color: #9B7BC4;
}

.builds-table {
    display: flex;
    flex-direction: column;
}

.builds-table-header {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 1.5fr 1.5fr 3fr 3fr;
    gap: 12px;
    padding: 6px 8px;
    background-color: #F7F9FB;
    border-radius: 6px;
    border: 1px solid #E9E9E9;
    font-family: Manrope;
    font-size: 11px;
    font-weight: 700;
    color: #7C7C7C;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.builds-table-row {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 1.5fr 1.5fr 3fr 3fr;
    gap: 12px;
    padding: 10px 8px;
    border: 1px solid #E9E9E9;
    border-top: none;
    align-items: center;
    font-family: Manrope;
    font-size: 13px;
    color: #101010;

    &:last-child {
        border-radius: 0 0 6px 6px;
    }
}

/* A tick box column: the supplier panels (pick upfront for emails and Delete) and deleted services (restore) */
.builds-table--selectable .builds-table-header,
.builds-table--selectable .builds-table-row {
    grid-template-columns: 40px 2fr 2fr 1fr 1.5fr 1.5fr 3fr 3fr;
}

.build-col-date {
    font-weight: 500;
    color: #7C7C7C;
    font-size: 12px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.build-col-rate {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.build-col-time {
    display: flex;
    align-items: center;
}

.build-col-service {
    font-weight: 600;
    padding-top: 6px;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    hyphens: auto;
}

.build-col-service-type {
    display: inline-flex;
    align-items: center;
    color: #3988AA;
    background-color: rgba(57, 136, 170, 0.1);
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    border-radius: 20px;
    padding: 2px 10px;
    margin-top: 4px;
    white-space: nowrap;
}

.build-col-checkbox {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
}

.build-input {
    max-width: 120px;
}

.build-textarea {
    :deep(textarea) {
        font-size: 12px;
        line-height: 1.4;
    }
}

.build-col-booking-notes {
    display: flex;
    align-items: center;
    padding-right: 20px;
}

.night-day-label {
    font-family: Manrope;
    font-size: 12px;
    font-weight: 600;
    color: #7C7C7C;
    min-width: 90px;
    white-space: nowrap;
}

.selected-builds-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.selected-build-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #F7F9FB;
    border: 1px solid #E9E9E9;
    border-radius: 6px;
    padding: 8px 12px;
}

.selected-build-name {
    font-family: Manrope;
    font-size: 13px;
    font-weight: 700;
    color: #101010;
}

.selected-build-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: Manrope;
    font-size: 12px;
    color: #7C7C7C;
    font-weight: 500;
}

.selected-build-meta span {
    display: flex;
    align-items: center;
    gap: 3px;
}

/* Attendance: rows shared by several packages (Copy Quote variants) and their per-package sub-rows */
.attendance-row--shared td {
    border-bottom: none !important;
}

.attendance-shared-badge {
    display: inline-block;
    margin-left: 8px;
    padding: 1px 8px;
    border-radius: 10px;
    background-color: #E8F2F6;
    color: #2a6a88;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
}

.attendance-subrow td {
    background-color: #F7FAFC;
    height: 32px !important;
    font-size: 12px !important;
    color: #5F6B73;
    border-bottom: none !important;
}

.attendance-subrow td:first-child {
    padding-left: 28px !important;
}

.attendance-subrow:last-child td,
.attendance-subrow + .attendance-row td {
    border-top: none;
}

.attendance-subrow-count {
    font-weight: 600;
    color: #2a6a88;
}

.attendance-subrow-empty {
    color: #C8C8C8;
}

/* Suppliers tab: each service is its own card, spaced apart */
.build-group {
    margin-top: 18px;
    border: 1px solid #E3E8EC;
    border-radius: 8px;
    overflow: hidden;
    background-color: #FFFFFF;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}

.build-group .builds-table-row {
    border: none;
    border-radius: 0;
    padding-top: 14px;
    padding-bottom: 14px;
}

/* Shared by several packages: a blue edge ties the main, variant and total rows together */
.build-group--shared {
    border-left: 4px solid #3988AA;
}

/* Suppliers tab: a service shared by several packages. The service row is the main package,
   then a row per variant package, then the supplier's total (what the supplier is sent). */
.builds-table-row--shared {
    border-bottom: none;
}

.package-row-name {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
    color: #3D3D3D;
}

.package-row-name--main {
    margin-top: 8px;
}

.package-row-label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #3988AA;
}

.builds-table-row.package-row {
    background-color: #F7FAFC;
    border-top: 1px dashed #D6E4EC;
    padding-top: 12px;
    padding-bottom: 12px;
}

.builds-table-row.package-row .package-row-name {
    padding-left: 8px;
}

.package-not-staying {
    display: flex;
    align-items: center;
    height: 40px;
    padding-left: 4px;
    font-size: 12px;
    font-style: italic;
    color: #B0B0B0;
}

:deep(.build-input--package .v-text-field__suffix) {
    font-size: 11px;
    font-weight: 600;
    color: #9E9E9E;
    white-space: nowrap;
}

/* Supplier total: one line, label then a chip per night */
.package-total-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    padding: 10px 16px 10px 56px;
    background-color: #EEF5F8;
    border-top: 1px solid #C9DEE8;
    font-family: Manrope;
}

.package-total-label {
    display: flex;
    flex-direction: column;
    min-width: 150px;
}

.package-total-hint {
    font-size: 11px;
    font-weight: 500;
    color: #7C7C7C;
}

.package-total-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.package-total-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    background-color: #FFFFFF;
    border: 1px solid #C9DEE8;
    border-radius: 14px;
    font-size: 12px;
    color: #2a6a88;
}

.package-total-chip-date {
    color: #7C7C7C;
    font-weight: 600;
}

.package-total-chip b {
    font-size: 14px;
}
</style>
